import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { gsap } from 'gsap';
import { BadgeCheck, Coins, Cpu, Play, RotateCw, Sparkles } from 'lucide-react';
import { useAudio } from '../poker-ui/AudioProvider';
import { ArcadeModeBackdrop } from '../poker-ui/ArcadeModeBackdrop';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { PlayingCard } from '../poker-ui/PlayingCard';
import {
  buildBelotFourMobileStackSlots,
  buildEllipticSeatSlots,
  getSeatArenaTuning,
} from '../poker-ui/seatLayout';
import {
  seatDisplayPercents as computeSeatDisplayPercents,
  SEAT_OFFSET_STORAGE,
} from '../poker-ui/seatLayoutOffsets';
import { cn } from '../poker-ui/utils/cn';
import {
  auctionTrumpBubbleGlyph,
  belotCardIsTrump,
  belotMatchCpFromEnginePoints,
  belotMatchTargetCpFromEngine,
  formatAuctionBid,
  sortBelotHandForDisplay,
} from '../game/belotArmenianCards';
import {
  belotAdvanceBelaSpeechState,
  belotFreshBelaRoyalsByTeam,
  belotSeatBubblePlacement,
} from '../game/belotBelaFx';
import { useBelotVisualFxQueue, BELOT_FX_PRIORITY } from './online/hooks/useBelotVisualFxQueue';
import { ArmenianAuctionPanel } from './belot/ArmenianAuctionPanel';
import {
  ArmenianBelotScoreSheetPanel,
} from './belot/ArmenianBelotScoreSheetPanel';
import { ArmenianBelotEngineDebugPanel } from './belot/ArmenianBelotEngineDebugPanel';
import { ArmenianBelotTablePresentation } from './belot/ArmenianBelotTablePresentation';
import { getTrickCenterDropMetric } from './belot/belotTrickDropMetric';
import {
  belotComboLabelShort,
  hasRevealableDeclaredComboCardIds,
  parseDeclaredComboKey,
  sortDeclaredComboKeysForRevealDescending,
} from './belot/belotComboLabelUi';
import { IGRA_ROOM_ASSETS } from './belot/igraRoomAssetUrls';
import { BelotRoundNotebookModal } from './belot/BelotRoundNotebookModal';
import { BelotHardcoreDeclareCombosPanel } from './belot/BelotHardcoreDeclareCombosPanel';
import { BelotPortraitMatchBar } from './belot/BelotPortraitMatchBar';
import { useBelotComboAnnounceCardReveal } from './belot/hooks/useBelotComboAnnounceCardReveal';
import { useBelotArmenianController } from './belot/hooks/useBelotArmenianController';
import { useBelotArmenianBots } from './belot/hooks/useBelotArmenianBots';
import { useBelotSeatLayout } from './belot/hooks/useBelotSeatLayout';
import { useBelotHeroTurnTimer } from './belot/hooks/useBelotHeroTurnTimer';
import { useBelotRoundLifecycle } from './belot/hooks/useBelotRoundLifecycle';
import { useBelotArmenianAnimations } from './belot/hooks/useBelotArmenianAnimations';
import { useBelotDealAnimation } from './belot/hooks/useBelotDealAnimation';
import { useBelotTrickAnimations } from './belot/hooks/useBelotTrickAnimations';
import { useBelotPhaseManager } from './belot/hooks/useBelotPhaseManager';
import { useBelotPreGameOrchestrator } from './belot/hooks/useBelotPreGameOrchestrator';
import './belot/ArmenianAuctionRevealFx.css';
import { getApiBaseUrl } from '../lib/apiBaseUrl';

function BelotCurrencyToast({ message, kind = 'info', onClose }) {
  const onCloseRef = useRef(onClose);
  const [leaving, setLeaving] = useState(false);
  const [coinImgMissing, setCoinImgMissing] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 2300);
    const closeTimer = setTimeout(() => onCloseRef.current?.(), 2700);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  const text = String(message || '');
  const isDebit = kind === 'debit';
  const isCredit = kind === 'credit';
  const isCurrencyToast = isDebit || isCredit;
  const shellClass = isDebit
    ? 'border-red-400/45 from-[#3a141a]/96 via-[#281018]/96 to-[#15080c]/96 shadow-[0_12px_34px_rgba(220,38,38,0.28)]'
    : isCredit
      ? 'border-emerald-300/55 from-[#10351f]/96 via-[#0f2b1b]/96 to-[#0a1a10]/96 shadow-[0_12px_34px_rgba(34,197,94,0.34)]'
      : 'border-cyan-300/45 from-[#10364d]/96 via-[#0e283b]/96 to-[#0a1726]/96 shadow-[0_12px_32px_rgba(34,211,238,0.26)]';
  const iconClass = isDebit
    ? 'border-red-300/35 bg-red-500/18 text-rose-100'
    : isCredit
      ? 'border-emerald-300/45 bg-emerald-500/20 text-yellow-200'
      : 'border-cyan-300/35 bg-cyan-500/15 text-cyan-100';
  const Icon = isCurrencyToast ? Coins : BadgeCheck;
  const coinImgSrc = `${process.env.PUBLIC_URL || ''}/1fishka.png`;
  const toneClass = isDebit ? 'text-rose-100' : isCredit ? 'text-yellow-200' : 'text-cyan-50';
  const shift = leaving ? '-translate-y-1 scale-[0.985] opacity-0' : 'translate-y-0 scale-100 opacity-100';

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.7rem,env(safe-area-inset-top,0px))] z-[130] w-auto max-w-[min(calc(100%-2.25rem),22rem)] -translate-x-1/2">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-gradient-to-br px-3 py-2 backdrop-blur-md transition-all duration-300 ease-out',
          'animate-[app-toast-in_360ms_cubic-bezier(0.2,0.8,0.2,1)]',
          shellClass,
          shift
        )}
        role={isDebit ? 'alert' : 'status'}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/18 to-transparent" aria-hidden />
        <div className="relative z-[1] flex items-center gap-2.5">
          <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border', iconClass)}>
            {isCurrencyToast ? (
              !coinImgMissing ? (
                <img
                  src={coinImgSrc}
                  alt=""
                  width={20}
                  height={20}
                  className="h-5 w-5 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                  onError={() => setCoinImgMissing(true)}
                  draggable={false}
                  aria-hidden
                />
              ) : <Icon className="h-4.5 w-4.5" aria-hidden />
            ) : (
              <Icon className="h-4.5 w-4.5" aria-hidden />
            )}
          </span>
          <p className={cn('min-w-0 break-words leading-snug text-[0.95rem] font-black tracking-[0.02em]', toneClass)}>
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

const { createInitialState, applyCommand } = require('../shared/game/belot/armenian/engine');
const { TRUMP_NO_TRUMP, TRUMP_ALL_TRUMP } = require('../shared/game/belot/armenian/auction');
const { legalPlays } = require('../shared/game/belot/armenian/tricks');
const { teamOfSeat, cardSuit } = require('../shared/game/belot/armenian/types');
const { resolveDeclarationBattleOutcome } = require('../game/belotArmenian/resolveDeclarationBattleOutcome');
const {
  detectCombinations,
  canDeclareBellaOnPlay,
  BELLA_POINTS,
} = require('../shared/game/belot/armenian/declarations');
const { clampBidPoints } = require('../game/belotMoldovan/moldovanBidding');
const {
  detectDeclarations: detectMoldovanDeclarations,
  applySequencePrecedence,
} = require('../game/belotMoldovan/moldovanDeclarations');
const { assertTotalsInvariant: assertMoldovanTotalsInvariant } = require('../game/belotMoldovan/moldovanScoring');

/**
 * @param {any} prevState
 * @param {any} nextState
 * @param {{ type?: string, payload?: Record<string, unknown> }[]} events
 */
function extractRoundSummary(prevState, nextState, events) {
  const ev = events.find((e) => e.type === 'ROUND_SCORED');
  if (!ev?.payload || typeof ev.payload !== 'object') return null;
  const p = /** @type {{ delta?: [number, number], success?: boolean, bait?: boolean, contractTeamTrickPlusDecl?: number }} */ (
    ev.payload
  );
  return {
    delta: Array.isArray(p.delta) ? p.delta : [0, 0],
    success: Boolean(p.success),
    bait: Boolean(p.bait),
    contractTeamTrickPlusDecl: Number(p.contractTeamTrickPlusDecl) || 0,
    teamScoresAfter: nextState.teamScores,
    tricksWonByTeam: prevState.tricksWonByTeam,
    contractTeam: prevState.contract?.contractTeam,
    contractTargetPoints: prevState.contract?.contractTargetPoints,
    contraMultiplier: prevState.contract?.contraMultiplier ?? 1,
    kapot: Boolean(prevState.contract?.kapot),
    auctionBid: prevState.auction?.highestBid ?? null,
  };
}

const HUMAN_SEAT = 0;
/** Синхрон с `useBelotArmenianController` — восстановление лога торгов после перезагрузки вкладки. */
const AUCTION_LOG_SESSION_KEY = 'seka:belotArm:auctionLog';
const LOCAL_SHULER_STORAGE_KEY = 'seka:belotArm:localShulerEnabled';
const LOCAL_BOT_EMOTES_STORAGE_KEY = 'seka:belotArm:botEmotesEnabled';

/** @param {string} key @param {boolean} defaultVal */
function readBoolLocalStorage(key, defaultVal) {
  try {
    const v = localStorage.getItem(key);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {
    /* ignore */
  }
  return defaultVal;
}
/** Задержка хода бота: должна покрывать полёт карты (~0.42s) + «размышление». */
const CARD_PLAY_FLIGHT_SEC = 0.46;
/** Полёт одной карты от колоды к слоту руки (как в локальной секе). */
const DEAL_HERO_BURST_MS = 105;
/** Пауза между картами при раздаче герою. */
const DEAL_HERO_STAGGER_MS = 22;
/** Пауза между игроками при полной раздаче 4×8. */
const DEAL_BETWEEN_SEATS_MS = 85;
/** Порядок раздачи по местам (визуально всем подряд). */
const DEAL_SEAT_ORDER = [0, 1, 2, 3];
/** Громкость `card-deal` при раздаче — как `DEAL_CARD_SOUND_VOL` в LocalGame. */
const DEAL_CARD_SOUND_VOL = 0.78;
/** Секунд на ход героя в торгах и во взятках (как в классическом локальном белоте). */
const HERO_TURN_SECONDS = 30;
const LOCAL_STAKE_MIN = 100;
const LOCAL_STAKE_MAX = 10000;
const LOCAL_STAKE_STEP = 100;
const LOCAL_RAKE_PERCENT = 2;
const DECLARE_VOTE_SECONDS = 10;
const AUCTION_REVEAL_MS = 2350;
const RANK_TO_PLAYING = ['7', '8', '9', 'J', 'Q', 'K', '10', 'A'];
/** Порядок мест на эллипсе (как в классическом `BelotLobby`). Герой — место 0 внизу. */
const SEAT_LAYOUT_ORDER = [1, 2, 0, 3];

function normalizeBelotTotals(totals, fallbackStake) {
  const safeStake = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Math.trunc(Number(fallbackStake) || LOCAL_STAKE_MIN)));
  const fallbackTeamStake = safeStake * 2;
  const fallbackTotalPot = fallbackTeamStake * 2;
  const fallbackRakeAmount = Math.floor(fallbackTotalPot * (LOCAL_RAKE_PERCENT / 100));
  const fallbackPayoutPool = fallbackTotalPot - fallbackRakeAmount;
  const fallbackWinnerShare = Math.floor(fallbackPayoutPool / 2);
  const src = totals && typeof totals === 'object' ? totals : {};
  const rakePercent = Number.isFinite(Number(src.rakePercent)) ? Math.max(0, Math.floor(Number(src.rakePercent))) : LOCAL_RAKE_PERCENT;
  return {
    playerStake: Number.isFinite(Number(src.playerStake)) ? Math.max(0, Math.floor(Number(src.playerStake))) : safeStake,
    teamStake: Number.isFinite(Number(src.teamStake)) ? Math.max(0, Math.floor(Number(src.teamStake))) : fallbackTeamStake,
    totalPot: Number.isFinite(Number(src.totalPot)) ? Math.max(0, Math.floor(Number(src.totalPot))) : fallbackTotalPot,
    rakePercent,
    rakeAmount: Number.isFinite(Number(src.rakeAmount)) ? Math.max(0, Math.floor(Number(src.rakeAmount))) : fallbackRakeAmount,
    payoutPool: Number.isFinite(Number(src.payoutPool)) ? Math.max(0, Math.floor(Number(src.payoutPool))) : fallbackPayoutPool,
    winnerShare: Number.isFinite(Number(src.winnerShare)) ? Math.max(0, Math.floor(Number(src.winnerShare))) : fallbackWinnerShare,
  };
}

/** Доп. сдвиг стопки к якорю победителя после фазы «куча». */
const TRICK_WIN_FLY = {
  top: { x: 0, y: -58 },
  left: { x: -76, y: 0 },
  bottom: { x: 0, y: 62 },
  right: { x: 76, y: 0 },
};

/** Ключ блока смещений мест в `SEAT_OFFSET_STORAGE.local` (4 игрока). */
const ARMENIAN_SEAT_STORAGE_BLOCK = 'armenian-belot-4';

function getRelativeSeatPosition(seatIndex, heroIndex) {
  if (seatIndex < 0 || heroIndex < 0) return 'top';
  const offset = (seatIndex - heroIndex + 4) % 4;
  if (offset === 0) return 'bottom';
  if (offset === 1) return 'left';
  if (offset === 2) return 'top';
  return 'right';
}

function toPlayingSuitFromLetter(suitLetter) {
  if (suitLetter === 'H') return 'hearts';
  if (suitLetter === 'D') return 'diamonds';
  if (suitLetter === 'C') return 'clubs';
  return 'spades';
}

/**
 * Полёт рубашки от колоды к слоту руки (паттерн как в `LocalGame` / раздача секи).
 * @param {HTMLElement} deckEl
 * @param {HTMLElement} targetEl
 * @param {() => void} onFlightStartSound
 * @param {'sm' | 'md'} [cardSize='md']
 */
function flyHeroDealCardFromDeck(deckEl, targetEl, onFlightStartSound, cardSize = 'md') {
  return new Promise((resolve) => {
    const deckRect = deckEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    if (deckRect.width < 2 || targetRect.width < 2) {
      resolve();
      return;
    }
    onFlightStartSound();
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = `${deckRect.left}px`;
    wrapper.style.top = `${deckRect.top}px`;
    wrapper.style.width = `${deckRect.width}px`;
    wrapper.style.height = `${deckRect.height}px`;
    wrapper.style.zIndex = '120';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.transformOrigin = 'top left';
    document.body.appendChild(wrapper);
    const root = createRoot(wrapper);
    root.render(<PlayingCard faceDown size={cardSize} />);
    requestAnimationFrame(() => {
      try {
        const tr = targetEl.getBoundingClientRect();
        const wr = wrapper.getBoundingClientRect();
        if (wr.width < 1) {
          try {
            root.unmount();
          } catch {
            /* ignore */
          }
          try {
            wrapper.remove();
          } catch {
            /* ignore */
          }
          resolve();
          return;
        }
        const tx = tr.left - wr.left;
        const ty = tr.top - wr.top;
        const sx = tr.width / Math.max(wr.width, 1);
        const sy = tr.height / Math.max(wr.height, 1);
        const anim = wrapper.animate(
          [
            { transform: 'translate(0px, 0px) scale(1, 1)' },
            { transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})` },
          ],
          {
            duration: DEAL_HERO_BURST_MS,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'forwards',
          },
        );
        anim.finished
          .catch(() => {})
          .finally(() => {
            try {
              root.unmount();
            } catch {
              /* ignore */
            }
            try {
              wrapper.remove();
            } catch {
              /* ignore */
            }
            resolve();
          });
      } catch {
        try {
          root.unmount();
        } catch {
          /* ignore */
        }
        try {
          wrapper.remove();
        } catch {
          /* ignore */
        }
        resolve();
      }
    });
  });
}

/** @param {number} cardId */
function cardIdToPlaying(cardId) {
  const suitIdx = Math.floor(cardId / 8);
  const rankIdx = cardId % 8;
  const letter = ['C', 'D', 'H', 'S'][suitIdx];
  return { rank: RANK_TO_PLAYING[rankIdx], suit: toPlayingSuitFromLetter(letter) };
}

/** @param {any} state */
function humanCanContra(state) {
  if (state.phase !== 'auction' || !state.auction) return false;
  if (state.auction.phase !== 'bidding') return false;
  if (state.auction.highestBid == null || state.auction.bidAuthorSeat == null) return false;
  if (state.auction.contraMultiplier !== 1) return false;
  const ct = teamOfSeat(state.auction.bidAuthorSeat);
  return teamOfSeat(HUMAN_SEAT) !== ct;
}

/** @param {any} state */
function humanCanRecontra(state) {
  if (state.phase !== 'auction' || !state.auction) return false;
  if (state.auction.phase !== 'bidding') return false;
  if (state.auction.highestBid == null || state.auction.bidAuthorSeat == null) return false;
  if (state.auction.contraMultiplier !== 2) return false;
  const ct = teamOfSeat(state.auction.bidAuthorSeat);
  return teamOfSeat(HUMAN_SEAT) === ct;
}

/** Команда с контрактом: места 0+2 (низ/верх) и 1+3 — подпись как на компасе стола. */
function contractPlayingPairLabel(contractTeam) {
  if (contractTeam === 0) return 'Юг – Север';
  if (contractTeam === 1) return 'Запад – Восток';
  return '';
}

/** @param {unknown} trump из контракта: 0..3 или `no_trump` */
function largeTrumpDisplay(trump) {
  if (trump === TRUMP_ALL_TRUMP || trump === 'all_trump') {
    return { line1: 'AT', line2: 'все козыри', red: false };
  }
  if (trump === TRUMP_NO_TRUMP || trump === 'no_trump') {
    return { line1: 'БК', line2: 'без козыря', red: false };
  }
  const n = Number(trump);
  if (Number.isInteger(n) && n >= 0 && n <= 3) {
    const sym = ['♣', '♦', '♥', '♠'][n];
    return { line1: sym, line2: null, red: n === 1 || n === 2 };
  }
  return { line1: '—', line2: null, red: false };
}

const DECL_RANK_LABEL = ['7', '8', '9', 'J', 'Q', 'K', '10', 'A'];

function formatSequenceReason(decl) {
  if (!decl || decl.kind !== 'SEQUENCE') return '—';
  const high = Number.isInteger(decl.highRank) && decl.highRank >= 0 && decl.highRank < DECL_RANK_LABEL.length
    ? DECL_RANK_LABEL[decl.highRank]
    : '?';
  const base =
    decl.label === 'CENT' ? 'Сотня' : decl.label === 'CINQUANTE' ? 'Полтинник' : 'Терц';
  return `${base} (до ${high})`;
}

function seatDisplayName(seat, heroName) {
  if (seat === HUMAN_SEAT) return heroName;
  return `Бот ${seat}`;
}

/** @param {{ seat: number, card: number, dirty?: boolean, caughtResolved?: boolean }[]} plays */
function playsToTrickUiRows(plays, heroName) {
  if (!Array.isArray(plays)) return [];
  return plays.map((p) => ({
    seat: p.seat,
    playCardId: p.card,
    key: `${p.seat}-${p.card}`,
    label: p.seat === HUMAN_SEAT ? heroName : `Бот ${p.seat}`,
    dirty: Boolean(p.dirty) && !p.caughtResolved,
    ...cardIdToPlaying(p.card),
  }));
}

/** @param {unknown} value @param {boolean} moldovan — возвращает порог движка (×10 к отображаемым CP). */
function normalizeInitialTargetScore(value, moldovan) {
  const n = Number(value);
  if (moldovan) {
    if (n === 5010 || n === 501) return 5010;
    return 10010;
  }
  if (n === 3010 || n === 301) return 3010;
  return 1510;
}

function normalizeInitialStake(value) {
  const raw = Math.trunc(Number(value) || LOCAL_STAKE_MIN);
  const clamped = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, raw));
  return Math.round(clamped / LOCAL_STAKE_STEP) * LOCAL_STAKE_STEP;
}

function normalizeInitialBotPlayMode(value) {
  return value === 'pro' ? 'pro' : 'simple';
}

function normalizeInitialComboMode(value) {
  return value === 'hardcore' ? 'hardcore' : 'casual';
}

function BelotArmenianLocalLobbyInner({
  user,
  onBack,
  onSwitchClassic,
  onUserPatch,
  autoStart,
  modeVariant = 'armenian',
  initialTargetScore,
  initialStake,
  initialBotPlayMode,
  initialDeclarationComboMode,
}) {
  const { play } = useAudio();
  const isMoldovanMode = modeVariant === 'moldovan';
  const heroName = useMemo(() => {
    const n = (user?.username || 'Игрок').trim();
    return n || 'Игрок';
  }, [user?.username]);

  const startTargetScore = useMemo(
    () => normalizeInitialTargetScore(initialTargetScore, isMoldovanMode),
    [initialTargetScore, isMoldovanMode],
  );
  const startStake = useMemo(() => normalizeInitialStake(initialStake), [initialStake]);
  const startBotPlayMode = useMemo(() => normalizeInitialBotPlayMode(initialBotPlayMode), [initialBotPlayMode]);
  const startComboMode = useMemo(() => {
    if (!isMoldovanMode) return 'hardcore';
    return normalizeInitialComboMode(initialDeclarationComboMode);
  }, [isMoldovanMode, initialDeclarationComboMode]);

  const [targetScore, setTargetScore] = useState(startTargetScore);
  const [selectedStake, setSelectedStake] = useState(startStake);
  const [botPlayMode, setBotPlayMode] = useState(startBotPlayMode);
  /** @type {'casual'|'hardcore'} */
  const [declarationComboMode, setDeclarationComboMode] = useState(startComboMode);
  const effectiveDeclarationMode = isMoldovanMode ? declarationComboMode : 'hardcore';
  const [sessionRngSeed] = useState(() => (Math.random() * 0x100000000) >>> 0);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [localShulerEnabled, setLocalShulerEnabled] = useState(() =>
    readBoolLocalStorage(LOCAL_SHULER_STORAGE_KEY, false),
  );
  const [botReactionsEnabled, setBotReactionsEnabled] = useState(() =>
    readBoolLocalStorage(LOCAL_BOT_EMOTES_STORAGE_KEY, true),
  );
  const [voidLeadCue, setVoidLeadCue] = useState(/** @type {null | { seat: number, leadSuit: number, token: number }} */ (null));
  const [shulerTableFx, setShulerTableFx] = useState(
    /** @type {null | { kind: 'dirty_play'|'caught'|'false_accuse', seats: number[], token: number }} */ (null),
  );
  const [botEmoteCue, setBotEmoteCue] = useState(
    /** @type {null | { seat: number, emoji: string, token: number }} */ (null),
  );
  const [matchFxBurst, setMatchFxBurst] = useState(false);
  const prevMatchDoneRef = useRef(false);
  const [engineState, setEngineState] = useState(() =>
    createInitialState({
      targetScore: startTargetScore,
      rngSeed: sessionRngSeed,
      declarationMode: startComboMode,
      shulerEnabled: readBoolLocalStorage(LOCAL_SHULER_STORAGE_KEY, false),
    }),
  );
  const activeTrumpSuit = engineState.contract?.trumpSuit === TRUMP_ALL_TRUMP
    ? null
    : (engineState.contract?.trumpSuit ?? null);
  const detectCombosForMode = useCallback((handCards) => {
    if (!Array.isArray(handCards) || handCards.length !== 8) return [];
    if (isMoldovanMode) {
      return detectMoldovanDeclarations(handCards, activeTrumpSuit);
    }
    return detectCombinations(handCards);
  }, [activeTrumpSuit, isMoldovanMode]);
  const [hardcoreDeclDialogOpen, setHardcoreDeclDialogOpen] = useState(false);
  const [banner, setBanner] = useState('');
  const [oppTurnClock, setOppTurnClock] = useState(null);
  const [, setOppRingTick] = useState(0);
  const [trickAnim, setTrickAnim] = useState(null);
  const [lastTrickSnapshot, setLastTrickSnapshot] = useState(null);
  const [lastRoundSummary, setLastRoundSummary] = useState(null);
  const [bellaNextPlay, setBellaNextPlay] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [scoreSheetRows, setScoreSheetRows] = useState([]);
  const [scoreSheetOpen, setScoreSheetOpen] = useState(false);
  const [engineDebugOpen, setEngineDebugOpen] = useState(false);
  const [engineDebugLog, setEngineDebugLog] = useState(
    /** @type {Array<{ seq: number, at: string, cmd: unknown, events: unknown[], stateAfter: Record<string, unknown>|null, ok: boolean, error?: string }>} */ ([]),
  );
  const [peekLastTrickOpen, setPeekLastTrickOpen] = useState(false);
  const [roundNotebookDismissed, setRoundNotebookDismissed] = useState(false);
  const lastTrickLongPressRef = useRef(null);
  const trickOverlayCardRefs = useRef([]);
  const trickCollectTlRef = useRef(null);
  const trickRevealToCollectRef = useRef(null);
  const stateRef = useRef(engineState);
  const applyRef = useRef(null);
  const seatArenaRef = useRef(null);
  const {
    seatArrangeMode,
    setSeatArrangeMode,
    seatOffsetBySeat,
    effectiveLayoutPreset,
    handleSeatDragPointerDown,
    handleSeatDragPointerMove,
    handleSeatDragPointerUp,
    handleSeatDragLostCapture,
    handleClearSeatOffsets,
  } = useBelotSeatLayout({
    sessionStarted,
    seatArenaRef,
    storageScope: SEAT_OFFSET_STORAGE.local,
    storageBlock: ARMENIAN_SEAT_STORAGE_BLOCK,
  });
  const prevTrickLenRef = useRef(0);
  const lastFourTrickRef = useRef(null);
  const handSlotRefs = useMemo(() => Array.from({ length: 8 }, () => React.createRef()), []);
  /** DOM контейнер места — цель полёта раздачи к аватару (без слотов карт у соперников). */
  const seatNodeRefsForDeal = useRef({});
  const deckRef = useRef(null);
  const potAnchorRef = useRef(null);
  const dealAnimGenRef = useRef(0);
  const dealFlightAbortRef = useRef(false);
  const lastBumpedDealGenRef = useRef(-1);
  /** Пока true — нет ходов (торги/взятки), идёт раздача; карты появляются по слотам как в локальной секе. */
  const [roundDealAnimActive, setRoundDealAnimActive] = useState(false);
  /** 4×8: слот раскрыт после полёта от колоды; null — не фаза по-слотовой раздачи. */
  const [seatDealSlotRevealed, setSeatDealSlotRevealed] = useState(
    /** @type {boolean[][] | null} */ (null),
  );
  const [, setDealVisualEpoch] = useState(0);
  const [, setDealFinishedNonce] = useState(0);
  const trickCenterRef = useRef(null);
  const heroSeatAnchorRef = useRef(null);
  const seatAnchorRefs = useRef({});
  const pickPlayCmdForSeatRef = useRef(null);
  const pendingPlayCmdRef = useRef(null);
  const suppressNextCardClickRef = useRef(false);
  const suppressClickTimeoutRef = useRef(null);
  const dragPlayRef = useRef(null);
  const dragMoveRafRef = useRef(null);
  const dragPointerRef = useRef({ x: 0, y: 0 });
  const dragDropActiveRef = useRef(false);
  const dragCancelTimeoutRef = useRef(null);
  const dragGhostRef = useRef(null);
  const dragSnapTweenRef = useRef(null);
  const flyCardWrapRef = useRef(null);
  const flyTweenRef = useRef(null);
  const contractBadgeAnchorRef = useRef(null);
  const [cardFly, setCardFly] = useState(null);
  const [pendingHiddenCardId, setPendingHiddenCardId] = useState(null);
  const [dragPlayCard, setDragPlayCard] = useState(null);
  const [lastSeatActions, setLastSeatActions] = useState(() => ({}));
  const [auctionSeatActions, setAuctionSeatActions] = useState(() => ({}));
  const [auctionLog, setAuctionLog] = useState([]);
  const [auctionLogOpen, setAuctionLogOpen] = useState(false);
  const fanPlayedRoundRef = useRef(-1);
  const matchStakeRef = useRef(LOCAL_STAKE_MIN);
  const matchTotalsRef = useRef(null);
  const matchSettledRef = useRef(false);
  const matchSessionIdRef = useRef(null);
  const [economyBusy, setEconomyBusy] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [entryDebitToast, setEntryDebitToast] = useState('');
  const [settlementDeltaAnimated, setSettlementDeltaAnimated] = useState(0);
  const [settlementDeltaTarget, setSettlementDeltaTarget] = useState(0);
  const [settlementAnimNonce, setSettlementAnimNonce] = useState(0);
  const [settlementAnimActive, setSettlementAnimActive] = useState(false);
  const [matchEndInlineError, setMatchEndInlineError] = useState('');
  const settlementAnimRafRef = useRef(null);
  const settlementAnimatedSessionRef = useRef(null);
  const autoStartTriggeredRef = useRef(false);

  useEffect(() => {
    stateRef.current = engineState;
  }, [engineState]);

  useEffect(() => {
    if (!sessionStarted || engineState.phase !== 'play' || engineState.currentTurnSeat !== HUMAN_SEAT) {
      setHardcoreDeclDialogOpen(false);
      return;
    }
    if (engineState.declarationMode !== 'hardcore') {
      setHardcoreDeclDialogOpen(false);
      return;
    }
    if (engineState.firstPlayDeclarationsCommitted?.[HUMAN_SEAT]) {
      setHardcoreDeclDialogOpen(false);
      return;
    }
    const h = engineState.handsAtRoundStart?.[HUMAN_SEAT];
    if (!Array.isArray(h) || h.length !== 8) return;
    let combos;
    try {
      combos = detectCombinations(h);
    } catch {
      return;
    }
    if (combos.length === 0) return;
    setHardcoreDeclDialogOpen(true);
  }, [
    sessionStarted,
    engineState.phase,
    engineState.currentTurnSeat,
    engineState.declarationMode,
    engineState.firstPlayDeclarationsCommitted,
    engineState.roundIndex,
    engineState.trickIndex,
    engineState.handsAtRoundStart,
  ]);

  const {
    auctionRevealFx,
    auctionRevealFallbackBanner,
    declareVoteReadyBySeat,
    declareVoteModalBlocked,
    declareVoteSecondsLeft,
    onAcceptDeclareVote,
    handleAuctionEndedReveal,
    preGameTransitionLocked,
  } = useBelotPreGameOrchestrator({
    engineState,
    humanSeat: HUMAN_SEAT,
    applyRef,
    stateRef,
    contractBadgeAnchorRef,
    play,
    largeTrumpDisplay,
    auctionRevealMs: AUCTION_REVEAL_MS,
    declareVoteSeconds: DECLARE_VOTE_SECONDS,
  });

  const visualFxQueue = useBelotVisualFxQueue({ defaultMinGapMs: 300 });
  const belaRoyalsByTeamRef = useRef(belotFreshBelaRoyalsByTeam());
  const lastDeclScoringRef = useRef({ a: 0, b: 0 });
  const localFxTickRef = useRef(0);
  const comboRevealLastKeyRef = useRef('');
  const prevTrickLenComboRef = useRef(0);
  const [belaSpeech, setBelaSpeech] = useState(null);
  const [comboRevealFx, setComboRevealFx] = useState(null);
  const [declareKeysSnapshot, setDeclareKeysSnapshot] = useState(null);
  const [casualDeclarationBattleOutcome, setCasualDeclarationBattleOutcome] = useState(
    /** @type {'team_a' | 'team_b' | 'tie' | null} */ (null),
  );
  const [hardcoreStripBattleApply, setHardcoreStripBattleApply] = useState(false);
  const comboRevealLaunchAnchorRef = useRef(/** @type {HTMLElement|null} */ (null));
  /** @type {React.MutableRefObject<Set<string>>} */
  const casualDeclareComboRevealDoneRef = useRef(new Set());
  /** То же ref (старое имя) — избегает ReferenceError при частичном HMR после переименования. */
  // eslint-disable-next-line no-unused-vars -- имя может вызываться из устаревшего hot-update чанка
  const casualDeclareTerzRevealDoneRef = casualDeclareComboRevealDoneRef;
  const hardcorePublicRevealDoneRoundsRef = useRef(/** @type {Set<number>} */ (new Set()));

  const prefersReducedMotionFx = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_SHULER_STORAGE_KEY, localShulerEnabled ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [localShulerEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_BOT_EMOTES_STORAGE_KEY, botReactionsEnabled ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [botReactionsEnabled]);

  useEffect(() => {
    setHardcoreStripBattleApply(false);
  }, [engineState.roundIndex]);

  const { revealPortal, playReveal } = useBelotComboAnnounceCardReveal({
    heroSeatAnchorRef,
    seatArenaRef,
  });

  useEffect(() => {
    casualDeclareComboRevealDoneRef.current = new Set();
    hardcorePublicRevealDoneRoundsRef.current = new Set();
  }, [engineState.roundIndex]);

  useEffect(() => {
    const rows = engineState.declaredComboKeysBySeat;
    if (engineState.phase === 'declare' && Array.isArray(rows)) {
      const has = rows.some((r) => Array.isArray(r) && r.length > 0);
      if (has) {
        setDeclareKeysSnapshot([0, 1, 2, 3].map((i) => (Array.isArray(rows[i]) ? rows[i].map((k) => String(k)) : [])));
      }
    }
    if (engineState.phase === 'init' || engineState.phase === 'dealing' || engineState.phase === 'auction') {
      setDeclareKeysSnapshot(null);
    }
  }, [engineState.phase, engineState.declaredComboKeysBySeat]);

  useEffect(() => {
    if (engineState.phase !== 'declare' || String(engineState.declarationMode || 'casual') !== 'casual') {
      return undefined;
    }
    const rows = Array.isArray(engineState.declaredComboKeysBySeat)
      ? engineState.declaredComboKeysBySeat
      : [];
    const r = Number(engineState.roundIndex) || 0;
    for (let seat = 0; seat < 4; seat += 1) {
      const keys = rows[seat];
      if (!Array.isArray(keys) || !keys.length) continue;
      for (const rawKey of keys) {
        const { label, labelRaw, cardIds } = parseDeclaredComboKey(rawKey);
        if (!hasRevealableDeclaredComboCardIds(cardIds)) continue;
        const dedupe = `casual-declare-combo-${r}-seat${seat}-${String(rawKey).replace(/\s+/g, '').slice(0, 80)}`;
        if (casualDeclareComboRevealDoneRef.current.has(dedupe)) continue;
        casualDeclareComboRevealDoneRef.current.add(dedupe);
        visualFxQueue.enqueue({
          id: dedupe,
          priority: BELOT_FX_PRIORITY.PRE_PLAY_COMBO_REVEAL,
          run: () =>
            new Promise((resolve) => {
              comboRevealLaunchAnchorRef.current = seatNodeRefsForDeal.current[seat] ?? null;
              playReveal({
                cardIds,
                label: label || undefined,
                comboLabelRaw: labelRaw || undefined,
                launchAnchorRef: comboRevealLaunchAnchorRef,
                timingPreset: 'normal',
                onComplete: resolve,
              });
            }),
        });
      }
    }
    return undefined;
  }, [
    engineState.phase,
    engineState.declarationMode,
    engineState.declaredComboKeysBySeat,
    engineState.roundIndex,
    visualFxQueue,
    playReveal,
  ]);

  useEffect(() => {
    if (engineState.phase !== 'play' || String(engineState.declarationMode || 'casual') !== 'hardcore') {
      return undefined;
    }
    const c = engineState.firstPlayDeclarationsCommitted;
    if (!Array.isArray(c) || c.length !== 4 || !c.every(Boolean)) return undefined;
    const r = Number(engineState.roundIndex) || 0;
    if (hardcorePublicRevealDoneRoundsRef.current.has(r)) return undefined;
    const rows = Array.isArray(engineState.declaredComboKeysBySeat)
      ? engineState.declaredComboKeysBySeat
      : [];
    let hasAny = false;
    for (let s = 0; s < 4; s += 1) {
      if (rows[s]?.length) {
        hasAny = true;
        break;
      }
    }
    if (!hasAny) {
      hardcorePublicRevealDoneRoundsRef.current.add(r);
      return undefined;
    }
    const tw = engineState.tricksWonByTeam;
    const tricksTakenTotal = (Number(tw?.[0]) || 0) + (Number(tw?.[1]) || 0);
    const currentTrickLen = Array.isArray(engineState.currentTrick) ? engineState.currentTrick.length : 0;
    const isCatchup = tricksTakenTotal > 0 || currentTrickLen > 0;
    const timingPreset = isCatchup ? 'catchup' : 'normal';
    const queueItem = isCatchup ? visualFxQueue.prepend.bind(visualFxQueue) : visualFxQueue.enqueue.bind(visualFxQueue);
    hardcorePublicRevealDoneRoundsRef.current.add(r);
    const seatOrder = [0, 2, 1, 3];
    for (const seat of seatOrder) {
      const keys = rows[seat];
      if (!Array.isArray(keys) || !keys.length) continue;
      const keysSorted = sortDeclaredComboKeysForRevealDescending(keys);
      for (let ki = 0; ki < keysSorted.length; ki += 1) {
        const rawKey = keysSorted[ki];
        const { label, labelRaw, cardIds } = parseDeclaredComboKey(rawKey);
        const ids = cardIds.length ? cardIds : [-1];
        const safeKey = String(rawKey).replace(/\s+/g, '').slice(0, 80);
        queueItem({
          id: `preplay-hardcore-local-${r}-seat${seat}-${safeKey}-${ki}`,
          priority: BELOT_FX_PRIORITY.PRE_PLAY_COMBO_REVEAL,
          run: () =>
            new Promise((resolve) => {
              comboRevealLaunchAnchorRef.current = seatNodeRefsForDeal.current[seat] ?? null;
              playReveal({
                cardIds: ids,
                label: label || undefined,
                comboLabelRaw: labelRaw || undefined,
                launchAnchorRef: comboRevealLaunchAnchorRef,
                timingPreset,
                onComplete: resolve,
              });
            }),
        });
      }
    }
    return undefined;
  }, [
    engineState.phase,
    engineState.declarationMode,
    engineState.firstPlayDeclarationsCommitted,
    engineState.declaredComboKeysBySeat,
    engineState.roundIndex,
    engineState.tricksWonByTeam,
    engineState.currentTrick,
    visualFxQueue,
    playReveal,
  ]);

  const onPostApplyFx = useCallback(
    ({ cmd, state, events }) => {
      if (cmd?.type === 'START_ROUND') {
        belaRoyalsByTeamRef.current = belotFreshBelaRoyalsByTeam();
        comboRevealLastKeyRef.current = '';
        casualDeclareComboRevealDoneRef.current = new Set();
        hardcorePublicRevealDoneRoundsRef.current = new Set();
        prevTrickLenComboRef.current = 0;
        setVoidLeadCue(null);
        setShulerTableFx(null);
        setBotEmoteCue(null);
        setCasualDeclarationBattleOutcome(null);
        return;
      }
      const evs = Array.isArray(events) ? events : [];
      for (const ev of evs) {
        if (ev?.type === 'DECLARATIONS_RESOLVED' && ev.payload && typeof ev.payload === 'object') {
          const p = ev.payload;
          lastDeclScoringRef.current = {
            a: Number(p.scoringA) || 0,
            b: Number(p.scoringB) || 0,
          };
          if (
            p.mode === 'casual' &&
            (p.outcome === 'team_a' || p.outcome === 'team_b' || p.outcome === 'tie')
          ) {
            setCasualDeclarationBattleOutcome(p.outcome);
          }
        }
      }
      for (const ev of evs) {
        if (!ev) continue;
        if (ev.type === 'CARD_PLAYED_DIRTY' && ev.payload && typeof ev.payload === 'object') {
          const seat = Number(/** @type {{ seat?: unknown }} */ (ev.payload).seat);
          if (Number.isInteger(seat) && seat >= 0 && seat <= 3) {
            const token = Date.now();
            setShulerTableFx({ kind: 'dirty_play', seats: [seat], token });
            window.setTimeout(() => {
              setShulerTableFx((cur) => (cur && cur.token === token ? null : cur));
            }, 1400);
          }
        } else if (ev.type === 'DIRTY_CAUGHT' && ev.payload && typeof ev.payload === 'object') {
          const p = /** @type {{ accuserSeat?: unknown, cheaterSeat?: unknown }} */ (ev.payload);
          const acc = Number(p.accuserSeat);
          const ch = Number(p.cheaterSeat);
          const seats = [];
          if (Number.isInteger(acc) && acc >= 0 && acc <= 3) seats.push(acc);
          if (Number.isInteger(ch) && ch >= 0 && ch <= 3 && ch !== acc) seats.push(ch);
          if (seats.length) {
            const token = Date.now();
            setShulerTableFx({ kind: 'caught', seats, token });
            window.setTimeout(() => {
              setShulerTableFx((cur) => (cur && cur.token === token ? null : cur));
            }, 2200);
          }
        } else if (ev.type === 'DIRTY_FALSE_ACCUSE' && ev.payload && typeof ev.payload === 'object') {
          const acc = Number(/** @type {{ accuserSeat?: unknown }} */ (ev.payload).accuserSeat);
          if (Number.isInteger(acc) && acc >= 0 && acc <= 3) {
            const token = Date.now();
            setShulerTableFx({ kind: 'false_accuse', seats: [acc], token });
            window.setTimeout(() => {
              setShulerTableFx((cur) => (cur && cur.token === token ? null : cur));
            }, 1800);
          }
        }
      }
      if (state.phase !== 'play' || !state.contract) return;
      const trump = state.contract.trumpSuit ?? state.contract.trump;
      const trumpSuit = state.contract.trumpSuit;
      for (const ev of evs) {
        if (!ev || ev.type !== 'CARD_PLAYED' || !ev.payload) continue;
        const pl = ev.payload;
        const seat = Number(pl.seat);
        const cardId = pl.card;
        const trickIndex = Number(pl.trickIndex);
        if (!Number.isInteger(seat) || seat < 0 || seat > 3 || cardId == null) continue;
        const adv = belotAdvanceBelaSpeechState({
          trump,
          cardId,
          seat,
          royalsPlayedByTeam: belaRoyalsByTeamRef.current,
        });
        belaRoyalsByTeamRef.current = adv.royalsPlayedByTeam;
        if (adv.kind === 'blot' || adv.kind === 'reblot') {
          localFxTickRef.current += 1;
          const tick = localFxTickRef.current;
          const line1 = adv.kind === 'blot' ? 'БЛОТ' : 'РЕБЛОТ';
          const glyph = auctionTrumpBubbleGlyph(trump);
          const token = Date.now();
          const place = belotSeatBubblePlacement(HUMAN_SEAT, seat);
          visualFxQueue.enqueue({
            id: `bela-local-${tick}-${seat}-${String(cardId)}-${Number.isFinite(trickIndex) ? trickIndex : 0}`,
            priority: BELOT_FX_PRIORITY.BELA_SPEECH,
            run: () =>
              new Promise((resolve) => {
                setBelaSpeech({
                  seat,
                  line1,
                  line2: glyph,
                  token,
                  tailUp: place.tailUp,
                  towardCenterPx: place.towardCenterPx,
                  driftX: place.driftX,
                  driftY: place.driftY,
                });
                window.setTimeout(() => {
                  setBelaSpeech((cur) => (cur && cur.token === token ? null : cur));
                  resolve();
                }, 1500);
              }),
          });
        }

        const trick = Array.isArray(state.currentTrick) ? state.currentTrick : [];
        if (trick.length >= 2 && Number.isInteger(cardId)) {
          const leadSuit = cardSuit(trick[0].card);
          const cs = cardSuit(cardId);
          let isVoid = false;
          if (cs !== leadSuit) {
            if (trumpSuit === TRUMP_ALL_TRUMP || trumpSuit === 'all_trump') isVoid = true;
            else if (trumpSuit == null) isVoid = true;
            else if (cs !== trumpSuit) isVoid = true;
          }
          if (isVoid) {
            const tokenV = Date.now();
            setVoidLeadCue({ seat, leadSuit, token: tokenV });
            window.setTimeout(() => {
              setVoidLeadCue((cur) => (cur && cur.token === tokenV ? null : cur));
            }, prefersReducedMotionFx ? 650 : 820);
          }
        }

        if (botReactionsEnabled && seat !== HUMAN_SEAT && Number.isInteger(cardId)) {
          const emojis = ['🙂', '😎', '🤔', '👍', '😅', '🎯'];
          const emoji = emojis[(seat * 17 + cardId) % emojis.length];
          const tokenE = Date.now();
          visualFxQueue.enqueue({
            id: `bot-emote-${tokenE}-${seat}-${String(cardId)}`,
            priority: BELOT_FX_PRIORITY.BOT_SEAT_EMOTE,
            run: () =>
              new Promise((resolve) => {
                setBotEmoteCue({ seat, emoji, token: tokenE });
                window.setTimeout(() => {
                  setBotEmoteCue((cur) => (cur && cur.token === tokenE ? null : cur));
                  resolve();
                }, 1300);
              }),
          });
        }
      }
    },
    [visualFxQueue, botReactionsEnabled, prefersReducedMotionFx],
  );

  useEffect(() => {
    const phase = engineState.phase;
    const L = Array.isArray(engineState.currentTrick) ? engineState.currentTrick.length : 0;
    if (phase !== 'play') {
      prevTrickLenComboRef.current = L;
      return;
    }
    const prev = prevTrickLenComboRef.current;
    prevTrickLenComboRef.current = L;
    // Движок в одном apply переводит взятку 3→4 карты в пустой currentTrick; в UI длина 3→0, не 4→0.
    if (!(prev === 3 && L === 0)) return;
    const lc = engineState.lastCompletedTrick;
    const playsOk = Array.isArray(lc?.plays) && lc.plays.length === 4;
    const trickIdx = Number(lc?.trickIndex ?? -1);
    const tw = engineState.tricksWonByTeam;
    const tricksSum = (Number(tw?.[0]) || 0) + (Number(tw?.[1]) || 0);
    if (!playsOk || trickIdx < 0 || tricksSum !== 1) return;
    const roundIndexEngine = Number(engineState.roundIndex) || 0;
    const dedupeKey = `${roundIndexEngine}-${trickIdx}`;
    if (dedupeKey === comboRevealLastKeyRef.current) return;
    comboRevealLastKeyRef.current = dedupeKey;

    const rows = declareKeysSnapshot;
    const parts = [];
    if (Array.isArray(rows)) {
      for (let s = 0; s < 4; s += 1) {
        const row = rows[s];
        if (Array.isArray(row) && row.length) parts.push(...row.map((k) => String(k)));
      }
    }
    const uniq = [...new Set(parts)]
      .map((k) => belotComboLabelShort(String(k).split(':')[0]))
      .filter(Boolean)
      .slice(0, 10);
    const d = engineState.declarationPointsByTeam;
    let cpTens = 0;
    if (Array.isArray(d) && d.length >= 2) {
      cpTens = Math.round(((Number(d[0]) || 0) + (Number(d[1]) || 0)) / 10);
    } else {
      const { a, b } = lastDeclScoringRef.current;
      cpTens = Math.round((a + b) / 10);
    }
    if (cpTens <= 0 && uniq.length === 0) return;
    const cpFlyText = cpTens > 0 ? `+${cpTens} CP` : 'COMBO';
    localFxTickRef.current += 1;
    const tick = localFxTickRef.current;
    visualFxQueue.enqueue({
      id: `combo-reveal-local-${roundIndexEngine}-${trickIdx}-${tick}`,
      priority: BELOT_FX_PRIORITY.COMBO_REVEAL,
      run: () =>
        new Promise((resolve) => {
          const token = Date.now();
          const delayStart = prefersReducedMotionFx ? 0 : 100;
          window.setTimeout(() => {
            setComboRevealFx({
              token,
              labels: uniq.length ? uniq : ['Объявления'],
              cpFlyText,
              prefersReducedMotion: prefersReducedMotionFx,
            });
            const holdMs = prefersReducedMotionFx ? 650 : 2000;
            window.setTimeout(() => {
              setComboRevealFx((c) => (c && c.token === token ? null : c));
              resolve();
            }, holdMs);
          }, delayStart);
        }),
    });
  }, [
    engineState.phase,
    engineState.currentTrick,
    engineState.lastCompletedTrick,
    engineState.tricksWonByTeam,
    engineState.roundIndex,
    engineState.declarationPointsByTeam,
    declareKeysSnapshot,
    prefersReducedMotionFx,
    visualFxQueue,
  ]);

  const declaredComboKeysForStrip = useMemo(() => {
    if (engineState.phase === 'play' && declareKeysSnapshot) return declareKeysSnapshot;
    return engineState.declaredComboKeysBySeat;
  }, [engineState.phase, engineState.declaredComboKeysBySeat, declareKeysSnapshot]);

  const declarationStripBattleOutcome = useMemo(() => {
    if (String(engineState.declarationMode || 'casual') === 'hardcore') {
      const c = engineState.firstPlayDeclarationsCommitted;
      if (!Array.isArray(c) || c.length !== 4 || !c.every(Boolean)) return null;
      return resolveDeclarationBattleOutcome(engineState)?.outcome ?? null;
    }
    return casualDeclarationBattleOutcome;
  }, [engineState, casualDeclarationBattleOutcome]);

  const declarationStripBattleApply = useMemo(() => {
    if (String(engineState.declarationMode || 'casual') === 'hardcore') {
      return hardcoreStripBattleApply;
    }
    return (
      engineState.phase === 'play' &&
      Boolean(declareKeysSnapshot) &&
      casualDeclarationBattleOutcome != null
    );
  }, [
    engineState.declarationMode,
    engineState.phase,
    declareKeysSnapshot,
    casualDeclarationBattleOutcome,
    hardcoreStripBattleApply,
  ]);

  useEffect(() => {
    if (String(engineState.declarationMode || 'casual') !== 'hardcore') return undefined;
    const c = engineState.firstPlayDeclarationsCommitted;
    if (!Array.isArray(c) || c.length !== 4 || !c.every(Boolean)) return undefined;
    if (engineState.phase !== 'play') return undefined;
    let cancelled = false;
    void visualFxQueue.waitUntilIdle().then(() => {
      window.setTimeout(
        () => {
          if (!cancelled) setHardcoreStripBattleApply(true);
        },
        prefersReducedMotionFx ? 200 : 650,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [
    engineState.declarationMode,
    engineState.firstPlayDeclarationsCommitted,
    engineState.phase,
    engineState.roundIndex,
    visualFxQueue,
    prefersReducedMotionFx,
  ]);

  const { apply, afterEngineCommandApplied, confirmHardcoreDeclarations } = useBelotArmenianController({
    formatAuctionBid,
    extractRoundSummary,
    engineStateRef: stateRef,
    setEngineState,
    setBanner,
    setLastSeatActions,
    setLastRoundSummary,
    setScoreSheetRows,
    setEngineDebugLog,
    hardcoreChoiceByKey: {},
    setHardcoreDeclDialogOpen,
    setAuctionSeatActions,
    setAuctionLog,
    onAuctionEnded: handleAuctionEndedReveal,
    onPostApplyFx,
  });
  useEffect(() => {
    applyRef.current = apply;
  }, [apply]);

  const onAccuseDirtyPlay = useCallback(
    (playCardId) => {
      if (!Number.isInteger(playCardId)) return;
      try {
        apply({ type: 'ACCUSE_DIRTY', seat: HUMAN_SEAT, payload: { card: playCardId } });
      } catch (e) {
        setBanner(e instanceof Error ? e.message : String(e));
      }
    },
    [apply, setBanner],
  );

  const canHumanContra = useMemo(() => humanCanContra(engineState), [engineState]);

  const canHumanRecontra = useMemo(() => humanCanRecontra(engineState), [engineState]);

  useEffect(() => {
    if (engineState.phase !== 'auction') setAuctionLogOpen(false);
  }, [engineState.phase]);

  useLayoutEffect(() => {
    if (!sessionStarted) return;
    if (engineState.phase !== 'auction') return;
    if (auctionLog.length > 0) return;
    try {
      const raw = sessionStorage.getItem(AUCTION_LOG_SESSION_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || typeof data.fp !== 'string' || !Array.isArray(data.log)) return;
      const cur = `${Number(engineState.roundIndex) || 0}|${Number(engineState.dealerSeat) || 0}`;
      if (data.fp !== cur || !data.log.length) return;
      setAuctionLog(data.log);
    } catch (_) {
      /* ignore */
    }
  }, [
    sessionStarted,
    engineState.phase,
    engineState.roundIndex,
    engineState.dealerSeat,
    auctionLog.length,
    setAuctionLog,
  ]);

  useBelotDealAnimation({
    engineState,
    sessionStarted,
    humanSeat: HUMAN_SEAT,
    handSlotRefs,
    seatRefs: seatNodeRefsForDeal,
    seatCardSlotRefs: null,
    deckRef,
    play,
    dealCardSoundVol: DEAL_CARD_SOUND_VOL,
    dealSeatOrder: DEAL_SEAT_ORDER,
    dealHeroStaggerMs: DEAL_HERO_STAGGER_MS,
    dealBetweenSeatsMs: DEAL_BETWEEN_SEATS_MS,
    dealAnimGenRef,
    dealFlightAbortRef,
    lastBumpedDealGenRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    setDealVisualEpoch,
    setDealFinishedNonce,
    flyHeroDealCardFromDeck,
  });

  const { heroSecLeft, heroTimedTurn, prevHeroTimedTurnRef } = useBelotHeroTurnTimer({
    sessionStarted,
    roundDealAnimActive,
    engineState,
    humanSeat: HUMAN_SEAT,
    turnSeconds: HERO_TURN_SECONDS,
    stateRef,
    apply,
    pickPlayCmdForSeatRef,
    belotCardIsTrump,
    play,
  });

  const { runBotPlayCommand } = useBelotArmenianAnimations({
    apply,
    play,
    stateRef,
    seatAnchorRefs,
    trickCenterRef,
    pendingPlayCmdRef,
    setCardFly,
    cardIdToPlaying,
    belotCardIsTrump,
    flyCardWrapRef,
    flyTweenRef,
    cardFly,
    cardPlayFlightSec: CARD_PLAY_FLIGHT_SEC,
  });

  const clearLastTrickLongPress = useCallback(() => {
    if (lastTrickLongPressRef.current != null) {
      window.clearTimeout(lastTrickLongPressRef.current);
      lastTrickLongPressRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearLastTrickLongPress();
    },
    [clearLastTrickLongPress],
  );

  useEffect(() => {
    if (!peekLastTrickOpen) return;
    if (engineState.phase !== 'play' || (engineState.currentTrick?.length ?? 0) > 0) {
      setPeekLastTrickOpen(false);
    }
  }, [peekLastTrickOpen, engineState.phase, engineState.currentTrick, setPeekLastTrickOpen]);


  useBelotTrickAnimations({
    engineState,
    play,
    humanSeat: HUMAN_SEAT,
    setTrickAnim,
    trickAnim,
    setLastTrickSnapshot,
    setPeekLastTrickOpen,
    trickOverlayCardRefs,
    trickCollectTlRef,
    trickRevealToCollectRef,
    prevTrickLenRef,
    lastFourTrickRef,
    fanPlayedRoundRef,
    handSlotRefs,
    getRelativeSeatPosition,
    trickWinFly: TRICK_WIN_FLY,
  });


  const { pickPlayCmdForSeat } = useBelotArmenianBots({
    botPlayMode,
    sessionStarted,
    engineState,
    trickAnim,
    stateRef,
    roundDealAnimActive,
    pendingPlayCmdRef,
    setOppTurnClock,
    setOppRingTick,
    runBotPlayCommand,
    apply,
  });

  useEffect(() => {
    pickPlayCmdForSeatRef.current = pickPlayCmdForSeat;
  }, [pickPlayCmdForSeat]);

  const { startSession, nextRound, resetToMenu, beginNewMatchAfterEnd } = useBelotRoundLifecycle({
    createInitialState,
    applyCommand,
    apply,
    afterEngineCommandApplied,
    targetScore,
    declarationComboMode: effectiveDeclarationMode,
    shulerEnabled: localShulerEnabled,
    setSessionStarted,
    setBanner,
    setScoreSheetRows,
    setScoreSheetOpen,
    setLastRoundSummary,
    setEngineDebugOpen,
    setEngineDebugLog,
    setPeekLastTrickOpen,
    clearLastTrickLongPress,
    setEngineState,
    setSeatArrangeMode,
    setTrickAnim,
    setLastTrickSnapshot,
    setBellaNextPlay,
    prevHeroTimedTurnRef,
    prevTrickLenRef,
    lastFourTrickRef,
    dealAnimGenRef,
    dealFlightAbortRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    pendingPlayCmdRef,
    flyTweenRef,
    trickRevealToCollectRef,
    trickCollectTlRef,
    setCardFly,
    play,
  });

  const humanHandSorted = useMemo(() => {
    const h = engineState.hands?.[HUMAN_SEAT];
    if (!h) return [];
    return sortBelotHandForDisplay(h);
  }, [engineState.hands]);

  useEffect(() => {
    setHoveredCardIndex(null);
  }, [humanHandSorted]);

  const { phaseLabel, isHumanAuctionTurn, isHumanPlayTurn, inDeclare, roundIdle, matchDone } =
    useBelotPhaseManager({
      engineState,
      humanSeat: HUMAN_SEAT,
    });

  useEffect(() => {
    if (!roundIdle) setRoundNotebookDismissed(false);
  }, [roundIdle]);

  const roundNotebookOpen = roundIdle && !roundNotebookDismissed;

  useEffect(() => {
    if (matchDone && !prevMatchDoneRef.current) {
      setMatchFxBurst(true);
      play('notification', 0.52);
      const t = window.setTimeout(() => setMatchFxBurst(false), 2400);
      prevMatchDoneRef.current = true;
      return () => window.clearTimeout(t);
    }
    if (!matchDone) prevMatchDoneRef.current = false;
    return undefined;
  }, [matchDone, play]);

  const trickAnimBlocksInput =
    Boolean(trickAnim) && (trickAnim.phase === 'reveal' || trickAnim.phase === 'collect');
  const isInputBlocked = roundDealAnimActive || Boolean(cardFly) || preGameTransitionLocked || trickAnimBlocksInput;
  const balanceValue = Math.max(0, Math.trunc(Number(user?.balance) || 0));
  const hasEnoughBalance = balanceValue >= selectedStake;
  const currentMatchStake = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Math.trunc(Number(matchStakeRef.current) || LOCAL_STAKE_MIN)));
  const currentTotals = normalizeBelotTotals(matchTotalsRef.current, currentMatchStake);
  const currentPlayerStake = currentTotals.playerStake;
  const currentTotalPot = currentTotals.totalPot;
  const currentRakePercent = currentTotals.rakePercent;
  const currentRakeAmount = currentTotals.rakeAmount;
  const currentPayoutPool = currentTotals.payoutPool;
  const currentWinnerShare = currentTotals.winnerShare;
  const heroWonMatch = (Number(engineState.teamScores?.[0]) || 0) >= (Number(engineState.teamScores?.[1]) || 0);
  const settlementDeltaDisplay =
    settlementAnimActive || settlementDeltaAnimated !== 0
      ? settlementDeltaAnimated
      : settlementDeltaTarget;
  const hasJwtToken = useMemo(() => {
    try {
      return Boolean(localStorage.getItem('token'));
    } catch {
      return false;
    }
  }, []);

  const callBelotEconomyApi = useCallback(async (path, body) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('AUTH_REQUIRED');
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const code = String(data?.code || data?.error || `HTTP_${res.status}`);
      const err = new Error(code);
      // @ts-ignore
      err.payload = data;
      throw err;
    }
    return data;
  }, []);

  const startSessionWithStake = useCallback(async () => {
    if (!hasEnoughBalance) {
      setBanner(
        `Недостаточно фишек: нужно ${selectedStake.toLocaleString('ru-RU')}, доступно ${balanceValue.toLocaleString('ru-RU')}.`,
      );
      play('notification');
      return;
    }
    if (!hasJwtToken) {
      setBanner('Для локального белота с экономикой нужна авторизация аккаунта.');
      play('notification');
      return;
    }
    if (economyBusy) return;
    setEconomyBusy(true);
    setMatchEndInlineError('');
    setSettlementDeltaAnimated(0);
    setSettlementDeltaTarget(0);
    setSettlementAnimActive(false);
    settlementAnimatedSessionRef.current = null;
    let startData;
    try {
      if (isMoldovanMode) {
        assertMoldovanTotalsInvariant();
      }
      startData = await callBelotEconomyApi('/api/belot/local/session/start', {
        stake: selectedStake,
        modeVariant: isMoldovanMode ? 'moldovan' : 'armenian',
        targetScore,
        rulesVersion: isMoldovanMode ? 'moldovan-v1' : 'armenian-v1',
      });
    } catch (e) {
      const code = String(e?.message || '').toUpperCase();
      const payloadBalance = Number(e?.payload?.balance);
      if (Number.isFinite(payloadBalance)) {
        onUserPatch?.({ balance: Math.max(0, Math.trunc(payloadBalance)) });
      }
      if (code === 'AUTH_REQUIRED' || code === 'HTTP_401' || code === '401') {
        setBanner('Войдите в аккаунт, чтобы начать матч с серверной экономикой.');
      } else if (code === 'INSUFFICIENT_FUNDS') {
        setBanner('Недостаточно средств для выбранной ставки.');
      } else {
        setBanner(`Ошибка старта матча: ${String(e?.message || e)}`);
      }
      play('notification');
      setEconomyBusy(false);
      return;
    }
    matchStakeRef.current = selectedStake;
    matchTotalsRef.current = normalizeBelotTotals(startData?.session?.totals, selectedStake);
    matchSettledRef.current = false;
    matchSessionIdRef.current = String(startData?.session?.id || '');
    if (typeof startData?.balance === 'number' && Number.isFinite(startData.balance)) {
      onUserPatch?.({ balance: Math.max(0, Math.trunc(startData.balance)) });
    }
    setEntryDebitToast(`Вход в матч: -${selectedStake.toLocaleString('ru-RU')} фишек`);
    startSession();
    setEconomyBusy(false);
  }, [
    balanceValue,
    callBelotEconomyApi,
    economyBusy,
    hasEnoughBalance,
    hasJwtToken,
    isMoldovanMode,
    onUserPatch,
    play,
    selectedStake,
    startSession,
    targetScore,
  ]);

  useEffect(() => {
    if (!autoStart) return;
    if (sessionStarted) return;
    if (autoStartTriggeredRef.current) return;
    autoStartTriggeredRef.current = true;
    startSessionWithStake();
  }, [autoStart, sessionStarted, startSessionWithStake]);

  const beginNewMatchAfterEndWithStake = useCallback(async () => {
    if (economyBusy || isSettling) return;
    if (!hasEnoughBalance) {
      setBanner(
        `Недостаточно фишек: нужно ${selectedStake.toLocaleString('ru-RU')}, доступно ${balanceValue.toLocaleString('ru-RU')}.`,
      );
      play('notification');
      return;
    }
    if (!hasJwtToken) {
      setBanner('Для локального белота с экономикой нужна авторизация аккаунта.');
      play('notification');
      return;
    }
    setEconomyBusy(true);
    setMatchEndInlineError('');
    try {
      const startData = await callBelotEconomyApi('/api/belot/local/session/start', {
        stake: selectedStake,
        modeVariant: isMoldovanMode ? 'moldovan' : 'armenian',
        targetScore,
        rulesVersion: isMoldovanMode ? 'moldovan-v1' : 'armenian-v1',
      });
      matchStakeRef.current = selectedStake;
      matchTotalsRef.current = normalizeBelotTotals(startData?.session?.totals, selectedStake);
      matchSettledRef.current = false;
      matchSessionIdRef.current = String(startData?.session?.id || '');
      if (typeof startData?.balance === 'number' && Number.isFinite(startData.balance)) {
        onUserPatch?.({ balance: Math.max(0, Math.trunc(startData.balance)) });
      }
      setSettlementDeltaAnimated(0);
      setSettlementDeltaTarget(0);
      setSettlementAnimActive(false);
      settlementAnimatedSessionRef.current = null;
      setEntryDebitToast(`Вход в матч: -${selectedStake.toLocaleString('ru-RU')} фишек`);
      beginNewMatchAfterEnd();
    } catch (e) {
      const code = String(e?.message || '').toUpperCase();
      const payloadBalance = Number(e?.payload?.balance);
      if (Number.isFinite(payloadBalance)) {
        onUserPatch?.({ balance: Math.max(0, Math.trunc(payloadBalance)) });
      }
      if (code === 'INSUFFICIENT_FUNDS') {
        setBanner('Недостаточно средств для выбранной ставки.');
      } else if (code === 'AUTH_REQUIRED' || code === 'HTTP_401' || code === '401') {
        setBanner('Войдите в аккаунт, чтобы начать матч с серверной экономикой.');
      } else if (code.includes('BELOT_LOCAL_SESSION_EXISTS')) {
        setBanner(
          'На сервере всё ещё открыта прошлая сессия белота (синхронизация не закрыла её). Выйдите в лобби и зайдите снова; если повторится — сообщите в поддержку.',
        );
      } else {
        setBanner(`Ошибка старта нового матча: ${String(e?.message || e)}`);
      }
      setMatchEndInlineError(`Не удалось запустить новый матч: ${String(e?.message || e)}`);
      play('notification');
    } finally {
      setEconomyBusy(false);
    }
  }, [
    balanceValue,
    beginNewMatchAfterEnd,
    callBelotEconomyApi,
    economyBusy,
    hasEnoughBalance,
    hasJwtToken,
    isMoldovanMode,
    isSettling,
    onUserPatch,
    play,
    selectedStake,
    targetScore,
  ]);

  const onHumanPass = () => {
    if (!isHumanAuctionTurn || isInputBlocked) return;
    play('fold', 0.32);
    apply({ type: 'PASS', seat: HUMAN_SEAT });
  };

  const onHumanAuctionBid = useCallback(
    (bid) => {
      if (!isHumanAuctionTurn || isInputBlocked) return;
      if (isMoldovanMode) {
        const normalizedPoints = clampBidPoints((Number(bid?.tens) || 8) * 10);
        const moldovanBid = { ...bid, tens: Math.max(8, Math.trunc(normalizedPoints / 10)) };
        apply({ type: 'BID', seat: HUMAN_SEAT, bid: moldovanBid });
      } else {
        apply({ type: 'BID', seat: HUMAN_SEAT, bid });
      }
      play('belot-auction-click', 0.55);
    },
    [apply, isHumanAuctionTurn, isInputBlocked, isMoldovanMode, play],
  );

  const onHumanContra = () => {
    if (!isHumanAuctionTurn || !canHumanContra || isInputBlocked) return;
    play('belot-contra-hit', 0.72);
    apply({ type: 'CONTRA', seat: HUMAN_SEAT });
  };

  const onHumanRecontra = () => {
    if (!isHumanAuctionTurn || !canHumanRecontra || isInputBlocked) return;
    play('belot-auction-click', 0.62);
    apply({ type: 'RECONTRA', seat: HUMAN_SEAT });
  };

  const onPlayCard = useCallback(
    (cardId, dragDropFromPoint = null) => {
      if (!isHumanPlayTurn || isInputBlocked) return;
      if (pendingPlayCmdRef.current) return;
      const s0 = stateRef.current;
      if (
        s0.phase === 'play' &&
        s0.currentTurnSeat === HUMAN_SEAT &&
        s0.declarationMode === 'hardcore' &&
        !s0.firstPlayDeclarationsCommitted?.[HUMAN_SEAT]
      ) {
        const hd = s0.handsAtRoundStart?.[HUMAN_SEAT];
        if (Array.isArray(hd) && hd.length === 8) {
          try {
            if (detectCombosForMode(hd).length > 0) return;
          } catch {
            return;
          }
        }
      }
      const hand = engineState.hands?.[HUMAN_SEAT];
      const trump = engineState.contract?.trumpSuit ?? null;
      const payload = { card: cardId };
      if (bellaNextPlay && hand && canDeclareBellaOnPlay(cardId, hand, trump)) {
        payload.declareBella = true;
      }
      const cmd = { type: 'PLAY_CARD', seat: HUMAN_SEAT, payload };
      const i = humanHandSorted.indexOf(cardId);
      const fromEl = i >= 0 ? handSlotRefs[i]?.current : null;
      const toEl = trickCenterRef.current;
      const reduced =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      setBellaNextPlay(false);
      if (!fromEl || !toEl || reduced) {
        apply(cmd);
        const isTr = trump != null && belotCardIsTrump(trump, cardId);
        play(isTr ? 'belot-card-table-trump' : 'belot-card-table', 0.78);
        return;
      }
      const { rank, suit } = cardIdToPlaying(cardId);
      const fr = fromEl.getBoundingClientRect();
      const tr = toEl.getBoundingClientRect();
      setPendingHiddenCardId(cardId);
      pendingPlayCmdRef.current = cmd;
      const startX = dragDropFromPoint?.x ?? fr.left + fr.width / 2;
      const startY = dragDropFromPoint?.y ?? fr.top + fr.height / 2;
      setCardFly({
        key: Date.now(),
        x0: startX,
        y0: startY,
        x1: tr.left + tr.width / 2,
        y1: tr.top + tr.height / 2,
        rank,
        suit,
        fromDrag: Boolean(dragDropFromPoint),
      });
    },
    [
      apply,
      bellaNextPlay,
      detectCombosForMode,
      engineState.contract,
      engineState.hands,
      handSlotRefs,
      humanHandSorted,
      isHumanPlayTurn,
      isInputBlocked,
      play,
    ]
  );

  useEffect(() => {
    if (pendingHiddenCardId == null) return;
    const hand = engineState.hands?.[HUMAN_SEAT];
    if (!Array.isArray(hand)) {
      setPendingHiddenCardId(null);
      return;
    }
    if (!hand.includes(pendingHiddenCardId)) {
      setPendingHiddenCardId(null);
    }
  }, [engineState.hands, pendingHiddenCardId]);

  const getPointerDropMetric = useCallback(
    (x, y) =>
      getTrickCenterDropMetric(trickCenterRef.current, x, y, { inscribedEllipse: true }),
    [],
  );

  const clearDragPlayCard = useCallback(() => {
    if (dragMoveRafRef.current != null) {
      window.cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
      dragCancelTimeoutRef.current = null;
    }
    if (dragSnapTweenRef.current) {
      dragSnapTweenRef.current.kill();
      dragSnapTweenRef.current = null;
    }
    dragDropActiveRef.current = false;
    dragPlayRef.current = null;
    setDragPlayCard(null);
  }, []);

  const armSuppressNextCardClick = useCallback((ms = 320) => {
    suppressNextCardClickRef.current = true;
    if (suppressClickTimeoutRef.current != null) {
      window.clearTimeout(suppressClickTimeoutRef.current);
    }
    suppressClickTimeoutRef.current = window.setTimeout(() => {
      suppressClickTimeoutRef.current = null;
      suppressNextCardClickRef.current = false;
    }, ms);
  }, []);

  const onHeroCardPointerDown = useCallback(
    (event, cardId, idx, canPlay) => {
      if (!canPlay || !isHumanPlayTurn || isInputBlocked || pendingPlayCmdRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (dragCancelTimeoutRef.current != null) {
        window.clearTimeout(dragCancelTimeoutRef.current);
        dragCancelTimeoutRef.current = null;
      }
      if (dragSnapTweenRef.current) {
        dragSnapTweenRef.current.kill();
        dragSnapTweenRef.current = null;
      }
      const cardRect = event.currentTarget?.getBoundingClientRect?.();
      const originX = cardRect ? cardRect.left + cardRect.width / 2 : event.clientX;
      const originY = cardRect ? cardRect.top + cardRect.height / 2 : event.clientY;
      const { rank, suit } = cardIdToPlaying(cardId);
      dragDropActiveRef.current = false;
      dragPlayRef.current = {
        pointerId: event.pointerId,
        cardId,
        originIndex: idx,
        originX,
        originY,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
      };
      setDragPlayCard({
        cardId,
        originIndex: idx,
        x: event.clientX,
        y: event.clientY,
        originX,
        originY,
        rank,
        suit,
        dropActive: false,
        returning: false,
      });
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    },
    [isHumanPlayTurn, isInputBlocked],
  );

  const onHeroCardPointerMove = useCallback(
    (event) => {
      const d = dragPlayRef.current;
      if (!d || event.pointerId !== d.pointerId) return;
      const dx = event.clientX - d.startX;
      const dy = event.clientY - d.startY;
      const moved = Math.hypot(dx, dy) >= 6;
      if (moved && !d.moved) d.moved = true;
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      if (dragMoveRafRef.current != null) return;
      dragMoveRafRef.current = window.requestAnimationFrame(() => {
        dragMoveRafRef.current = null;
        const current = dragPointerRef.current;
        const metric = getPointerDropMetric(current.x, current.y);
        const nextDropActive =
          d.moved &&
          (dragDropActiveRef.current ? metric <= 1.16 : metric <= 1.0);
        dragDropActiveRef.current = nextDropActive;
        setDragPlayCard((prev) =>
          prev
            ? {
                ...prev,
                x: current.x,
                y: current.y,
                dropActive: nextDropActive,
              returning: false,
              }
            : prev,
        );
      });
    },
    [getPointerDropMetric],
  );

  const onHeroCardPointerUp = useCallback(
    (event) => {
      const d = dragPlayRef.current;
      if (!d || event.pointerId !== d.pointerId) return;
      const metric = getPointerDropMetric(event.clientX, event.clientY);
      const dropActive = dragDropActiveRef.current ? metric <= 1.16 : metric <= 1.0;
      const moved = Boolean(d.moved);
      const cardId = d.cardId;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      if (moved && !dropActive) {
        const ghostEl = dragGhostRef.current;
        const originX = d.originX ?? event.clientX;
        const originY = d.originY ?? event.clientY;
        armSuppressNextCardClick();
        if (ghostEl) {
          dragSnapTweenRef.current = gsap.to(ghostEl, {
            left: originX,
            top: originY,
            duration: 0.22,
            ease: 'power2.out',
            onComplete: () => {
              dragSnapTweenRef.current = null;
              clearDragPlayCard();
            },
          });
          setDragPlayCard((prev) =>
            prev ? { ...prev, returning: true, dropActive: false } : prev,
          );
          return;
        }
      }
      clearDragPlayCard();
      if (!moved) {
        armSuppressNextCardClick(220);
        onPlayCard(cardId);
        return;
      }
      armSuppressNextCardClick();
      if (dropActive) onPlayCard(cardId, { x: event.clientX, y: event.clientY });
    },
    [armSuppressNextCardClick, clearDragPlayCard, getPointerDropMetric, onPlayCard],
  );

  const onHeroCardPointerCancel = useCallback(() => {
    const d = dragPlayRef.current;
    if (!d) {
      clearDragPlayCard();
      return;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
    }
    dragCancelTimeoutRef.current = window.setTimeout(() => {
      dragCancelTimeoutRef.current = null;
      clearDragPlayCard();
    }, 90);
  }, [clearDragPlayCard]);

  const onHeroCardClick = useCallback((cardId) => {
    if (suppressNextCardClickRef.current) {
      suppressNextCardClickRef.current = false;
      if (suppressClickTimeoutRef.current != null) {
        window.clearTimeout(suppressClickTimeoutRef.current);
        suppressClickTimeoutRef.current = null;
      }
      return;
    }
    onPlayCard(cardId);
  }, [onPlayCard]);

  useEffect(
    () => () => {
      if (suppressClickTimeoutRef.current != null) {
        window.clearTimeout(suppressClickTimeoutRef.current);
        suppressClickTimeoutRef.current = null;
      }
      if (dragMoveRafRef.current != null) {
        window.cancelAnimationFrame(dragMoveRafRef.current);
        dragMoveRafRef.current = null;
      }
      if (dragCancelTimeoutRef.current != null) {
        window.clearTimeout(dragCancelTimeoutRef.current);
        dragCancelTimeoutRef.current = null;
      }
      if (dragSnapTweenRef.current) {
        dragSnapTweenRef.current.kill();
        dragSnapTweenRef.current = null;
      }
    },
    [],
  );

  const trickUi = useMemo(() => {
    const rows = engineState.currentTrick || [];
    return rows.map((p) => ({
      seat: p.seat,
      playCardId: p.card,
      key: `${p.seat}-${p.card}`,
      label: p.seat === HUMAN_SEAT ? heroName : `Бот ${p.seat}`,
      dirty: Boolean(p.dirty) && !p.caughtResolved,
      ...cardIdToPlaying(p.card),
    }));
  }, [engineState.currentTrick, heroName]);

  const seatArena = useMemo(() => getSeatArenaTuning(4, 'igra'), []);
  const seatSlots = useMemo(
    () => {
      if (effectiveLayoutPreset === 'mobile-stack')
        return buildBelotFourMobileStackSlots({ variant: 'igra' });
      return buildEllipticSeatSlots({
        seats: SEAT_LAYOUT_ORDER,
        heroSeat: HUMAN_SEAT,
        centerX: 50,
        centerY: seatArena.centerY,
        radiusX: seatArena.radiusX,
        radiusY: seatArena.radiusY,
      });
    },
    [effectiveLayoutPreset, seatArena.centerY, seatArena.radiusX, seatArena.radiusY],
  );

  const legalCardIds = useMemo(() => {
    if (!isHumanPlayTurn || !engineState.hands || !engineState.contract) return new Set();
    const hand = engineState.hands[HUMAN_SEAT];
    const lead = engineState.trickLeadSeat;
    if (lead == null) return new Set();
    const ids = legalPlays(hand, engineState.currentTrick, engineState.contract.trumpSuit, lead);
    return new Set(ids);
  }, [
    isHumanPlayTurn,
    engineState.hands,
    engineState.currentTrick,
    engineState.contract,
    engineState.trickLeadSeat,
  ]);

  const humanFirstPlayCommitPending = useMemo(() => {
    if (engineState.phase !== 'play' || engineState.currentTurnSeat !== HUMAN_SEAT) return false;
    if (engineState.declarationMode !== 'hardcore') return false;
    if (engineState.firstPlayDeclarationsCommitted?.[HUMAN_SEAT]) return false;
    const hd = engineState.handsAtRoundStart?.[HUMAN_SEAT];
    if (!Array.isArray(hd) || hd.length !== 8) return false;
    try {
      return detectCombosForMode(hd).length > 0;
    } catch {
      return false;
    }
  }, [
    engineState.phase,
    engineState.currentTurnSeat,
    engineState.declarationMode,
    engineState.firstPlayDeclarationsCommitted,
    engineState.handsAtRoundStart,
    detectCombosForMode,
  ]);

  const hardcoreDialogCombos = useMemo(() => {
    if (!hardcoreDeclDialogOpen) return [];
    const h = engineState.handsAtRoundStart?.[HUMAN_SEAT];
    if (!Array.isArray(h) || h.length !== 8) return [];
    try {
      return [...detectCombosForMode(h)].sort((a, b) => b.points - a.points);
    } catch {
      return [];
    }
  }, [hardcoreDeclDialogOpen, detectCombosForMode, engineState.handsAtRoundStart]);

  const moldovanDeclarationCompare = useMemo(() => {
    if (!isMoldovanMode || engineState.phase !== 'declare') return null;
    const hands = engineState.handsAtRoundStart;
    if (!Array.isArray(hands) || hands.length < 4) return null;
    try {
      const teamA = [...detectCombosForMode(hands[0]), ...detectCombosForMode(hands[2])];
      const teamB = [...detectCombosForMode(hands[1]), ...detectCombosForMode(hands[3])];
      const precedence = applySequencePrecedence(teamA, teamB);
      const seqA = teamA.filter((d) => d?.kind === 'SEQUENCE');
      const seqB = teamB.filter((d) => d?.kind === 'SEQUENCE');
      const teamASeqTotal = seqA.reduce((sum, d) => sum + (Number(d?.points) || 0), 0);
      const teamBSeqTotal = seqB.reduce((sum, d) => sum + (Number(d?.points) || 0), 0);
      const topA = seqA.reduce((best, cur) => {
        if (!best) return cur;
        const lenDiff = (Number(cur?.length) || 0) - (Number(best?.length) || 0);
        if (lenDiff !== 0) return lenDiff > 0 ? cur : best;
        const hiDiff = (Number(cur?.highRank) || -1) - (Number(best?.highRank) || -1);
        if (hiDiff !== 0) return hiDiff > 0 ? cur : best;
        return cur;
      }, null);
      const topB = seqB.reduce((best, cur) => {
        if (!best) return cur;
        const lenDiff = (Number(cur?.length) || 0) - (Number(best?.length) || 0);
        if (lenDiff !== 0) return lenDiff > 0 ? cur : best;
        const hiDiff = (Number(cur?.highRank) || -1) - (Number(best?.highRank) || -1);
        if (hiDiff !== 0) return hiDiff > 0 ? cur : best;
        return cur;
      }, null);
      return {
        precedence,
        topA,
        topB,
        teamASeqTotal,
        teamBSeqTotal,
      };
    } catch {
      return null;
    }
  }, [detectCombosForMode, engineState.handsAtRoundStart, engineState.phase, isMoldovanMode]);

  const bellaEligibleThisTurn = useMemo(() => {
    if (!isHumanPlayTurn || !engineState.hands?.[HUMAN_SEAT] || !engineState.contract) return false;
    const trump = engineState.contract.trumpSuit;
    if (trump == null) return false;
    const hand = engineState.hands[HUMAN_SEAT];
    const lead = engineState.trickLeadSeat;
    if (lead == null) return false;
    const legal = legalPlays(hand, engineState.currentTrick, trump, lead);
    return legal.some((c) => canDeclareBellaOnPlay(c, hand, trump));
  }, [
    isHumanPlayTurn,
    engineState.hands,
    engineState.currentTrick,
    engineState.contract,
    engineState.trickLeadSeat,
  ]);

  useEffect(() => {
    setBellaNextPlay(false);
  }, [isHumanPlayTurn, engineState.trickIndex, engineState.currentTrick?.length]);

  const heroTeamBellaSoFar = engineState.bellaBonusByTeam?.[teamOfSeat(HUMAN_SEAT)] ?? 0;
  const blotRebelotButtonLabel = isMoldovanMode
    ? `Белла (+${BELLA_POINTS})`
    : heroTeamBellaSoFar >= BELLA_POINTS
      ? `Реблот (+${BELLA_POINTS})`
      : `Блот (+${BELLA_POINTS})`;

  const auctionContraMult = engineState.auction?.contraMultiplier ?? 1;
  const auctionMultIsMax = auctionContraMult >= 4;
  const highestBidAuthorSeat = engineState.auction?.bidAuthorSeat;
  const highestBidAuthorLabel =
    Number.isInteger(highestBidAuthorSeat) ? seatDisplayName(highestBidAuthorSeat, heroName) : '';
  const highestBidAuthorRole =
    Number.isInteger(highestBidAuthorSeat)
      ? (teamOfSeat(highestBidAuthorSeat) === teamOfSeat(HUMAN_SEAT) ? 'Партнёр' : 'Оппонент')
      : '';

  const handFanMetrics = useMemo(() => {
    const handCount = humanHandSorted.length;
    const mobileStack = effectiveLayoutPreset === 'mobile-stack';
    const baseFanStep = mobileStack ? (handCount > 7 ? 36 : 41) : handCount > 7 ? 34 : 40;
    const desiredFanWidth = Math.max(mobileStack ? 268 : 240, (handCount - 1) * baseFanStep + 84);
    const maxFanWidth = mobileStack ? 368 : 340;
    const fanWidth = Math.min(desiredFanWidth, maxFanWidth);
    const fanStep = handCount > 1 ? (fanWidth - 84) / (handCount - 1) : 0;
    const fanBaseYOffset = mobileStack ? -38 : -50;
    return { handCount, fanWidth, fanStep, fanBaseYOffset };
  }, [humanHandSorted.length, effectiveLayoutPreset]);

  const statusMessage = useMemo(() => {
    if (banner) return banner;
    const p = engineState.phase;
    if (p === 'auction') {
      if (roundDealAnimActive) return null;
      if (isMoldovanMode) {
        if (engineState.currentTurnSeat === HUMAN_SEAT) return 'Ваш ход в bazar.';
        if (engineState.currentTurnSeat != null) {
          return `Bazar · ход: ${seatDisplayName(engineState.currentTurnSeat, heroName)}`;
        }
        return 'Bazar';
      }
      if (engineState.currentTurnSeat === HUMAN_SEAT) return 'Ваш ход в торгах.';
      if (engineState.currentTurnSeat != null) return `Торги · ход: ${seatDisplayName(engineState.currentTurnSeat, heroName)}`;
      return 'Торги';
    }
    if (p === 'declare') {
      if (isMoldovanMode) {
        return 'Сравнение деклараций перед первым ходом: подтвердите готовность к розыгрышу.';
      }
      return 'После «К взяткам» на первом вашем ходу выберите по каждой комбе — объявить или скрыть (без объявления очки сгорают).';
    }
    if (p === 'play') {
      if (engineState.currentTurnSeat === HUMAN_SEAT) {
        if (humanFirstPlayCommitPending) {
          return 'Сначала решите по каждой комбинации: объявить в зачёт или скрыть (диалог поверх стола).';
        }
        return 'Ваш ход — выберите карту.';
      }
      if (engineState.currentTurnSeat != null) {
        return `Взятка ${engineState.trickIndex}/8 · ход: ${seatDisplayName(engineState.currentTurnSeat, heroName)}`;
      }
      return `Взятка ${engineState.trickIndex}/8`;
    }
    if (p === 'dealing') {
      if (lastRoundSummary?.teamScoresAfter) {
        const [a, b] = lastRoundSummary.teamScoresAfter;
        return lastRoundSummary.success
          ? `Контракт сыгран. Счёт матча ${a} : ${b}. Детали ниже.`
          : `Контракт не взят${lastRoundSummary.bait ? ' (бейт)' : ''}. Счёт ${a} : ${b}. Детали ниже.`;
      }
      return 'Раунд посчитан. Нажмите «Следующий раунд».';
    }
    return phaseLabel;
  }, [
    banner,
    engineState.phase,
    engineState.currentTurnSeat,
    engineState.trickIndex,
    heroName,
    phaseLabel,
    isMoldovanMode,
    lastRoundSummary,
    roundDealAnimActive,
    humanFirstPlayCommitPending,
  ]);

  const potTricksTaken =
    (engineState.tricksWonByTeam?.[0] ?? 0) + (engineState.tricksWonByTeam?.[1] ?? 0);

  const matchEndWinnerLines = useMemo(() => {
    if (!matchDone) return [];
    const tgt = Number(engineState.targetScore) || 0;
    const s0 = Number(engineState.teamScores?.[0]) || 0;
    const s1 = Number(engineState.teamScores?.[1]) || 0;
    const lines = [];
    if (s0 >= tgt) lines.push('Победила команда A — вы и ваш партнёр.');
    if (s1 >= tgt) lines.push('Победила команда B — соперники.');
    if (!lines.length) lines.push('Матч завершён.');
    return lines;
  }, [matchDone, engineState.targetScore, engineState.teamScores]);

  const matchEndHeadline = useMemo(() => {
    if (!matchDone) return 'Матч завершён';
    const s0 = Number(engineState.teamScores?.[0]) || 0;
    const s1 = Number(engineState.teamScores?.[1]) || 0;
    if ((s0 > 0 && s1 === 0) || (s1 > 0 && s0 === 0)) {
      return 'Разгром! 🔥';
    }
    return 'Матч завершён';
  }, [matchDone, engineState.teamScores]);

  const fmtSheetDelta = (n) => {
    if (n === 0) return '0';
    if (n > 0) return `+${n}`;
    return String(n);
  };

  useEffect(() => {
    if (!matchDone || matchSettledRef.current) return;
    if (!matchSessionIdRef.current) return;
    setMatchEndInlineError('');
    setIsSettling(true);
    (async () => {
      try {
        const usScore = Number(engineState.teamScores?.[0]) || 0;
        const themScore = Number(engineState.teamScores?.[1]) || 0;
        const data = await callBelotEconomyApi('/api/belot/local/session/settle', {
          sessionId: matchSessionIdRef.current,
          usScore,
          themScore,
          targetScore: Number(engineState.targetScore) || targetScore,
          modeVariant: isMoldovanMode ? 'moldovan' : 'armenian',
          rulesVersion: isMoldovanMode ? 'moldovan-v1' : 'armenian-v1',
        });
        if (typeof data?.balance === 'number' && Number.isFinite(data.balance)) {
          onUserPatch?.({ balance: Math.max(0, Math.trunc(data.balance)) });
        }
        const nextTotals = normalizeBelotTotals(data?.session?.totals, matchStakeRef.current);
        matchTotalsRef.current = nextTotals;
        const heroWon = usScore >= themScore;
        const nextDelta = heroWon ? Number(nextTotals.winnerShare || 0) : -Number(nextTotals.playerStake || 0);
        setSettlementDeltaAnimated(0);
        setSettlementDeltaTarget(nextDelta);
        setSettlementAnimNonce((v) => v + 1);
        matchSettledRef.current = true;
      } catch (e) {
        const code = String(e?.message || '').toUpperCase();
        const payloadBalance = Number(e?.payload?.balance);
        if (Number.isFinite(payloadBalance)) {
          onUserPatch?.({ balance: Math.max(0, Math.trunc(payloadBalance)) });
        }
        if (code.includes('BELOT_LOCAL_BAD_MATCH_STATE')) {
          matchSettledRef.current = true;
          setBanner('Итоги уже зафиксированы на сервере.');
          setMatchEndInlineError('Итоги уже зафиксированы на сервере.');
        } else {
          const msg = `Ошибка синхронизации итогов матча с сервером: ${String(e?.message || e)}`;
          setBanner(msg);
          setMatchEndInlineError(msg);
        }
      } finally {
        setIsSettling(false);
      }
    })();
  }, [callBelotEconomyApi, engineState.targetScore, engineState.teamScores, isMoldovanMode, matchDone, onUserPatch, targetScore]);

  useEffect(() => {
    if (!matchDone || isSettling || !matchSettledRef.current) return;
    if (settlementAnimNonce <= 0) return;
    const sessionId = String(matchSessionIdRef.current || '');
    if (!sessionId || settlementAnimatedSessionRef.current === sessionId) return;
    if (settlementAnimRafRef.current != null) {
      cancelAnimationFrame(settlementAnimRafRef.current);
      settlementAnimRafRef.current = null;
    }
    settlementAnimatedSessionRef.current = sessionId;
    setSettlementAnimActive(true);
    setSettlementDeltaAnimated(0);
    let startTs = null;
    const from = 0;
    const to = Number(settlementDeltaTarget) || 0;
    const duration = Math.min(1800, Math.max(650, Math.abs(to) * 2.3));
    const tick = (ts) => {
      if (startTs == null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + (to - from) * eased);
      setSettlementDeltaAnimated(next);
      if (t < 1) {
        settlementAnimRafRef.current = requestAnimationFrame(tick);
      } else {
        setSettlementDeltaAnimated(to);
        setSettlementAnimActive(false);
        settlementAnimRafRef.current = null;
      }
    };
    settlementAnimRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (settlementAnimRafRef.current != null) {
        cancelAnimationFrame(settlementAnimRafRef.current);
        settlementAnimRafRef.current = null;
      }
    };
  }, [isSettling, matchDone, settlementAnimNonce, settlementDeltaTarget]);

  const handleLeaveRequest = useCallback(() => {
    if (!sessionStarted) {
      onBack();
      return;
    }
    if (matchDone || matchSettledRef.current) {
      onBack();
      return;
    }
    if (isSettling) {
      setBanner('Подождите: идёт синхронизация итогов матча.');
      return;
    }
    setLeaveError('');
    setLeaveConfirmOpen(true);
  }, [isSettling, matchDone, onBack, sessionStarted]);

  const confirmLeaveWithForfeit = useCallback(async () => {
    setLeaveConfirmOpen(false);
    setLeaveError('');
    try {
      if (matchSessionIdRef.current && !matchSettledRef.current) {
        setEconomyBusy(true);
        const data = await callBelotEconomyApi('/api/belot/local/session/forfeit', {
          sessionId: matchSessionIdRef.current,
        });
        if (typeof data?.balance === 'number' && Number.isFinite(data.balance)) {
          onUserPatch?.({ balance: Math.max(0, Math.trunc(data.balance)) });
        }
        matchTotalsRef.current = normalizeBelotTotals(data?.session?.totals, matchStakeRef.current);
      }
      setEconomyBusy(false);
      resetToMenu();
      onBack();
    } catch {
      setEconomyBusy(false);
      setLeaveError('Не удалось зафиксировать выход на сервере. Проверьте связь и повторите.');
      setLeaveConfirmOpen(true);
    }
  }, [callBelotEconomyApi, onBack, onUserPatch, resetToMenu]);

  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      {peekLastTrickOpen &&
      engineState.phase === 'play' &&
      (engineState.currentTrick?.length ?? 0) === 0 &&
      engineState.lastCompletedTrick?.plays?.length === 4 ? (
        <div
          className="fixed inset-0 z-[59] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          onClick={() => setPeekLastTrickOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-neon-gold/35 bg-[#0a1220]/96 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Последняя завершённая взятка"
          >
            <p className="text-center text-[11px] font-bold uppercase tracking-wide text-neon-gold">
              Последняя взятка · №{engineState.lastCompletedTrick.trickIndex}
            </p>
            <p className="mt-1 text-center text-xs text-slate-300">
              Забрал: {seatDisplayName(engineState.lastCompletedTrick.winnerSeat, heroName)}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {playsToTrickUiRows(engineState.lastCompletedTrick.plays, heroName).map((c) => {
                const isHeroPlay = c.seat === HUMAN_SEAT;
                return (
                  <div key={`peek-${c.key}`} className="flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        'rounded-xl',
                        isHeroPlay && 'p-[3px] ring-2 ring-cyan-300/95 ring-offset-0',
                      )}
                    >
                      <PlayingCard
                        rank={c.rank}
                        suit={c.suit}
                        size="sm"
                        className={cn('shadow-xl', c.dirty && 'ring-2 ring-amber-400/80')}
                      />
                    </div>
                    <span className="max-w-[5rem] truncate text-[10px] text-muted-foreground">{c.label}</span>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setPeekLastTrickOpen(false)}
              className="mt-5 w-full rounded-xl border border-white/15 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}

      <BelotRoundNotebookModal
        open={roundNotebookOpen}
        onClose={() => setRoundNotebookDismissed(true)}
        summary={lastRoundSummary}
        heroTeamIdx={teamOfSeat(HUMAN_SEAT)}
        targetScore={Number(engineState.targetScore) || targetScore}
      />

      {matchDone && !engineDebugOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4" aria-live="assertive">
          {matchFxBurst ? (
            <div className="pointer-events-none absolute inset-0 z-[61] overflow-hidden" aria-hidden>
              <div
                className={cn(
                  'pointer-events-none absolute left-1/2 top-[10%] z-[62] -translate-x-1/2 text-center',
                  heroWonMatch ? 'text-emerald-200' : 'text-rose-200',
                )}
              >
                <p className="text-[clamp(2rem,8vw,3.25rem)] font-black uppercase tracking-tight drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]">
                  {heroWonMatch ? 'Победа' : 'Поражение'}
                </p>
              </div>
              {Array.from({ length: 28 }).map((_, i) => (
                <span
                  key={`conf-${i}`}
                  className={cn(
                    'pointer-events-none absolute h-2 w-2 rounded-full opacity-90 motion-reduce:opacity-40',
                    ['bg-emerald-400', 'bg-amber-300', 'bg-cyan-300', 'bg-violet-400', 'bg-rose-400'][i % 5],
                    'motion-safe:animate-[belot-confetti-drop_2.2s_ease-out_forwards] motion-reduce:animate-none',
                  )}
                  style={{
                    left: `${4 + ((i * 37) % 92)}%`,
                    top: '-6%',
                    animationDelay: `${(i % 9) * 0.06}s`,
                  }}
                />
              ))}
            </div>
          ) : null}
          <div
            className={cn(
              'max-h-[min(92vh,640px)] w-full max-w-md overflow-y-auto rounded-2xl border bg-gradient-to-b px-5 py-6 text-center',
              heroWonMatch ? 'poker-fx-win-panel poker-fx-win-aura' : 'poker-fx-loss-panel',
              heroWonMatch
                ? 'border-emerald-400/45 from-emerald-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(34,197,94,0.25)]'
                : 'border-rose-400/45 from-rose-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(244,63,94,0.25)]',
            )}
          >
            <Sparkles className={cn('mx-auto mb-2 h-10 w-10', heroWonMatch ? 'text-emerald-300' : 'text-rose-300')} aria-hidden />
            <p className={cn('text-lg font-black', heroWonMatch ? 'text-emerald-50' : 'text-rose-50')}>{matchEndHeadline}</p>
            <p className="mt-2 font-mono text-2xl font-bold text-amber-100">
              {belotMatchCpFromEnginePoints(engineState.teamScores[0])} :{' '}
              {belotMatchCpFromEnginePoints(engineState.teamScores[1])}
            </p>
            <p className={cn('mt-1 text-xs', heroWonMatch ? 'text-emerald-100/85' : 'text-rose-100/85')}>
              Цель: {belotMatchTargetCpFromEngine(engineState.targetScore)} CP
            </p>
            <p className={cn('mt-1 text-[11px]', heroWonMatch ? 'text-emerald-100/80' : 'text-rose-100/80')}>
              Банк: {currentTotalPot} · Комиссия {currentRakePercent}%: {currentRakeAmount} · Выплата команде: {currentPayoutPool}
            </p>
            <p className={cn('text-[11px]', heroWonMatch ? 'text-emerald-100/85' : 'text-rose-100/85')}>
              {heroWonMatch
                ? `Чистый выигрыш на игрока: ${currentWinnerShare}`
                : `Списано с игрока: ${currentPlayerStake}`}{' '}
              фишек
            </p>
            <p className="text-[11px] font-semibold text-amber-100/90">
              {heroWonMatch ? 'Начисление баланса: ' : 'Списание баланса: '}
              <span className={cn('font-mono tabular-nums', heroWonMatch ? 'text-emerald-200' : 'text-rose-200')}>
                {settlementDeltaDisplay >= 0 ? '+' : ''}
                {settlementDeltaDisplay.toLocaleString('ru-RU')}
              </span>{' '}
              фишек
              {settlementAnimActive ? <span className="ml-1 animate-pulse text-amber-200/80">●</span> : null}
            </p>
            {isSettling ? (
              <p className="mt-1 text-[10px] font-semibold text-cyan-200/90">Синхронизация итогов с сервером...</p>
            ) : null}
            {matchEndInlineError ? (
              <p className="mt-1 rounded-lg border border-rose-400/35 bg-rose-950/35 px-2 py-1 text-[10px] font-semibold text-rose-100">
                {matchEndInlineError}
              </p>
            ) : null}
            <ul className={cn('mt-3 space-y-1 text-left text-xs leading-snug', heroWonMatch ? 'text-emerald-50/90' : 'text-rose-50/90')}>
              {matchEndWinnerLines.map((line, i) => (
                <li key={i}>• {line}</li>
              ))}
            </ul>
            <p className={cn('mt-2 text-[10px] leading-snug', heroWonMatch ? 'text-emerald-100/75' : 'text-rose-100/75')}>
              Готовы к следующему матчу?
            </p>
            {scoreSheetRows.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-900/35 bg-[#faf6ea] p-2 text-left text-amber-950 shadow-inner">
                <p className="mb-1 text-center font-serif text-[11px] font-bold text-amber-950">Листок партии</p>
                <table className="w-full border-collapse font-mono text-[10px] tabular-nums sm:text-xs">
                  <thead>
                    <tr className="border-b border-amber-900/25 bg-[#e8dcc4] font-serif">
                      <th className="px-1 py-1">#</th>
                      <th className="px-1 py-1">Мы</th>
                      <th className="px-1 py-1">Они</th>
                      <th className="hidden px-1 py-1 sm:table-cell">Σ</th>
                      <th className="hidden px-1 py-1 sm:table-cell">Σ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreSheetRows.map((r) => (
                      <tr key={r.roundOrdinal} className="border-b border-amber-900/10 last:border-0">
                        <td className="px-1 py-1">{r.roundOrdinal}</td>
                        <td className="px-1 py-1">{fmtSheetDelta(r.deltaUs)}</td>
                        <td className="px-1 py-1">{fmtSheetDelta(r.deltaThem)}</td>
                        <td className="hidden px-1 py-1 font-semibold sm:table-cell">{r.cumUs}</td>
                        <td className="hidden px-1 py-1 font-semibold sm:table-cell">{r.cumThem}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setEngineDebugOpen(true)}
                className="rounded-xl border border-cyan-400/45 bg-cyan-950/35 py-3 text-sm font-bold text-cyan-100 transition hover:bg-cyan-900/45"
              >
                Движок и история
              </button>
              <button
                type="button"
                onClick={beginNewMatchAfterEndWithStake}
                disabled={economyBusy || isSettling}
                className={cn(
                  'rounded-xl border py-3 text-sm font-bold transition',
                  economyBusy || isSettling
                    ? 'cursor-not-allowed border-amber-700/45 bg-amber-950/35 text-amber-200/65'
                    : 'border-amber-400/55 bg-amber-950/55 text-amber-100 hover:bg-amber-900/60',
                )}
              >
                {economyBusy ? 'Запуск нового матча...' : isSettling ? 'Подождите...' : 'Сыграть ещё'}
              </button>
              <button
                type="button"
                onClick={handleLeaveRequest}
                className="rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
      )}

      <ArcadeModeBackdrop mode="belot" />

      <GameIndustrialHeader
        floating
        chromeVariant={sessionStarted ? 'igra' : 'default'}
        tableName={`${isMoldovanMode ? 'Молдавский белот' : 'Армянский белот'} · ${heroName} vs боты`}
        blindsLabel={sessionStarted ? phaseLabel : 'Локальный движок'}
        playersCount={4}
        maxPlayers={4}
        bucksBalance={user?.bucksBalance}
        balance={Number.isFinite(Number(user?.balance)) ? Math.max(0, Math.floor(Number(user.balance))) : undefined}
        onLeave={handleLeaveRequest}
        onSeatArrangeToggle={sessionStarted ? () => setSeatArrangeMode((v) => !v) : undefined}
        seatArrangeMode={sessionStarted && seatArrangeMode}
        onSeatLayoutReset={sessionStarted ? handleClearSeatOffsets : undefined}
        extraControls={
          <button
            type="button"
            onClick={() => setEngineDebugOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-cyan-400/35 bg-cyan-500/10 text-cyan-100 transition hover:border-cyan-300/55 hover:bg-cyan-500/18"
            title="Движок: лог команд, состояние движка, история применений"
            aria-label="Открыть отладку движка белота"
          >
            <Cpu className="h-4 w-4" aria-hidden />
          </button>
        }
      />

      <main
        className={cn(
          'relative z-10 flex flex-1 flex-col overflow-hidden',
          sessionStarted ? 'min-h-0' : 'mx-auto w-full max-w-xl gap-3 overflow-y-auto px-3 pb-24 pt-3 sm:gap-3 sm:px-4',
        )}
        style={
          sessionStarted
            ? {
                backgroundColor: '#151311',
                backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.bg)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
              }
            : undefined
        }
      >
        {sessionStarted ? (
          <BelotPortraitMatchBar
            engineState={engineState}
            targetScore={engineState.targetScore ?? targetScore}
            contractBadgeRef={contractBadgeAnchorRef}
            onAuctionLogClick={() => setAuctionLogOpen((v) => !v)}
            contractPlayingPairLabel={contractPlayingPairLabel}
          />
        ) : null}
        {!sessionStarted ? (
          autoStart ? (
            <></>
          ) : (
            <>
            <div className="relative overflow-hidden rounded-2xl border border-violet-300/20 bg-gradient-to-br from-[#2a1647]/88 via-[#21113a]/84 to-[#170c2b]/88 px-3 py-2 shadow-[0_12px_28px_rgba(0,0,0,0.34)]">
              <div className="pointer-events-none absolute inset-0" aria-hidden>
                <div className="absolute left-[10%] top-[12%] h-16 w-16 rounded-full bg-violet-300/16 blur-xl" />
                <div className="absolute bottom-[10%] right-[12%] h-14 w-14 rounded-full bg-fuchsia-300/12 blur-xl" />
              </div>
              <img
                src="/belot-button.png"
                alt=""
                className="relative z-[1] mx-auto block h-auto max-h-[72px] w-full max-w-[min(320px,92%)] object-contain sm:max-h-[82px]"
                draggable={false}
              />
            </div>
            <div className="text-center">
              <p className="text-base font-black text-violet-100">
                {isMoldovanMode ? 'Молдавский белот (локально)' : 'Армянский белот (локально)'}
              </p>
              <p className="mx-auto mt-1 max-w-lg text-xs leading-relaxed text-slate-300/90">
                Вы на месте 0 с партнером-ботом на месте 2; соперники — боты 1 и 3.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card/60 p-3">
              <div>
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Цель матча</p>
                <div className="flex gap-2">
                  {(isMoldovanMode ? [5010, 10010] : [1510, 3010]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTargetScore(v)}
                      className={cn(
                        'flex-1 rounded-xl border py-2 text-sm font-bold transition',
                        targetScore === v
                          ? 'border-violet-400/70 bg-violet-500/20 text-violet-100'
                          : 'border-white/10 bg-[#16122a]/70 text-slate-300 hover:border-violet-300/35',
                      )}
                    >
                      {belotMatchTargetCpFromEngine(v)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Ставка на матч</p>
                <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-slate-400">Выбор ставки</span>
                  <span className="font-black text-violet-100">{selectedStake.toLocaleString('ru-RU')} фишек</span>
                </div>
                <input
                  type="range"
                  min={LOCAL_STAKE_MIN}
                  max={LOCAL_STAKE_MAX}
                  step={LOCAL_STAKE_STEP}
                  value={selectedStake}
                  onChange={(e) =>
                    setSelectedStake(
                      Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Number(e.target.value) || LOCAL_STAKE_MIN)),
                    )
                  }
                  className="w-full accent-violet-400"
                  aria-label={isMoldovanMode ? 'Ставка в молдавском белоте' : 'Ставка в армянском белоте'}
                />
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500">
                  <span>{LOCAL_STAKE_MIN.toLocaleString('ru-RU')}</span>
                  <span>{LOCAL_STAKE_MAX.toLocaleString('ru-RU')}</span>
                </div>
                <p className={cn('mt-1.5 text-[11px]', hasEnoughBalance ? 'text-slate-400' : 'text-rose-300')}>
                  Баланс: {balanceValue.toLocaleString('ru-RU')} фишек
                  {!hasEnoughBalance ? ' · Недостаточно для старта' : ''}
                </p>
              </div>

              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Боты во взятках</p>
                <div className="flex gap-2" role="group" aria-label="Сложность ботов во взятках">
                  {[
                    { id: 'simple', label: 'Простые' },
                    { id: 'pro', label: 'Умные' },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setBotPlayMode(id)}
                      className={cn(
                        'flex-1 rounded-xl border py-2 text-xs font-bold transition sm:text-sm',
                        botPlayMode === id
                          ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-100'
                          : 'border-white/10 bg-[#16122a]/70 text-slate-300 hover:border-cyan-300/35',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Эффекты стола</p>
                <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-white/10 bg-[#16122a]/60 px-2.5 py-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-violet-400"
                    checked={localShulerEnabled}
                    disabled={sessionStarted}
                    onChange={(e) => setLocalShulerEnabled(e.target.checked)}
                  />
                  <span className="text-left text-[11px] leading-snug text-slate-300">
                    <span className="font-bold text-violet-100">Режим шулера</span> — незаконные ходы не блокируются;
                    грязная карта подсвечивается; двойной клик по ней с места героя — обвинение (
                    <span className="font-mono text-[10px]">ACCUSE_DIRTY</span>).
                  </span>
                </label>
                <label className="mt-2 flex cursor-pointer items-start gap-2.5 rounded-lg border border-white/10 bg-[#16122a]/60 px-2.5 py-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-cyan-400"
                    checked={botReactionsEnabled}
                    disabled={sessionStarted}
                    onChange={(e) => setBotReactionsEnabled(e.target.checked)}
                  />
                  <span className="text-left text-[11px] leading-snug text-slate-300">
                    <span className="font-bold text-cyan-100">Реакции ботов</span> — короткие эмодзи над ботами после хода.
                  </span>
                </label>
              </div>

              {isMoldovanMode ? (
                <div className="mt-3 border-t border-white/10 pt-3">
                  <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                    Режим комбинаций
                  </p>
                  <div className="flex gap-2" role="group" aria-label="Режим комбинаций">
                    {[
                      { id: 'casual', label: 'Казуальный' },
                      { id: 'hardcore', label: 'Хардкор' },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        disabled={sessionStarted}
                        onClick={() => setDeclarationComboMode(id)}
                        className={cn(
                          'flex-1 rounded-xl border py-2 text-xs font-bold transition sm:text-sm',
                          sessionStarted && 'cursor-not-allowed opacity-50',
                          declarationComboMode === id
                            ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-100'
                            : 'border-white/10 bg-[#16122a]/70 text-slate-300 hover:border-emerald-300/35',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <details className="rounded-2xl border border-violet-300/25 bg-gradient-to-b from-violet-500/[0.06] to-transparent px-3.5 py-3 text-xs text-slate-300">
              <summary className="cursor-pointer list-none text-[11px] font-black uppercase tracking-[0.14em] text-violet-200">
                О режиме
              </summary>
              <p className="mt-2 leading-relaxed text-slate-300/90">
                {isMoldovanMode
                  ? 'Тот же движок, что на сервере: торги от 100, взятки, комбинации (казуальный или хардкор с выбором на первом ходу).'
                  : 'Базар Блот: торги от 100 (Capot 250), жёсткие взятки, комбинации объявляются на первом ходу — не объявил, сгорело.'}
              </p>
            </details>

            <div className="sticky bottom-2 z-20 space-y-2 rounded-2xl border border-violet-300/25 bg-[#0f0a1e]/90 p-2.5 shadow-[0_10px_26px_rgba(0,0,0,0.35)] backdrop-blur">
              <button
                type="button"
                onClick={startSessionWithStake}
                disabled={!hasEnoughBalance || economyBusy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-violet-300/45 bg-gradient-to-b from-violet-500/25 to-violet-700/20 py-3 text-sm font-black text-violet-100 shadow-[0_12px_28px_rgba(139,92,246,0.22)]"
              >
                <Play className="h-4 w-4" aria-hidden />
                Начать матч
              </button>
              {!hasJwtToken ? (
                <p className="text-center text-[11px] text-amber-200/85">
                  Для старта матча нужен вход в аккаунт (JWT).
                </p>
              ) : null}
              {banner ? (
                <div className="rounded-xl border border-rose-400/35 bg-rose-950/20 px-3 py-2 text-center text-[11px] text-rose-100">
                  {banner}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  onSwitchClassic?.();
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border/70 py-2.5 text-xs font-semibold text-muted-foreground transition hover:border-violet-300/35 hover:text-violet-200"
              >
                Классический локальный белот
              </button>
            </div>
            </>
          )
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <ArmenianBelotTablePresentation
                engineState={engineState}
                gameRoomSkin="igra"
                suppressIgraDecorativeTopChrome={sessionStarted}
                onBackToLobby={handleLeaveRequest}
                showStickyHud={false}
                phaseLabel={phaseLabel}
                targetScore={Number(engineState.targetScore) || targetScore}
                banner={banner}
                roundIdle={roundIdle}
                suppressIdleStatusBanner={roundIdle}
                heroTimedTurn={heroTimedTurn}
                statusMessage={statusMessage}
                heroName={heroName}
                setScoreSheetOpen={setScoreSheetOpen}
                setEngineDebugOpen={setEngineDebugOpen}
                engineDebugOpen={engineDebugOpen}
                setPeekLastTrickOpen={setPeekLastTrickOpen}
                clearLastTrickLongPress={clearLastTrickLongPress}
                lastTrickLongPressRef={lastTrickLongPressRef}
                heroSecLeft={heroSecLeft}
                auctionContraMult={auctionContraMult}
                seatArenaRef={seatArenaRef}
                seatArena={seatArena}
                seatArrangeMode={seatArrangeMode}
                potTricksTaken={potTricksTaken}
                deckRef={deckRef}
                potAnchorRef={potAnchorRef}
                seatSlots={seatSlots}
                seatOffsetBySeat={seatOffsetBySeat}
                handleSeatDragPointerDown={handleSeatDragPointerDown}
                handleSeatDragPointerMove={handleSeatDragPointerMove}
                handleSeatDragPointerUp={handleSeatDragPointerUp}
                handleSeatDragLostCapture={handleSeatDragLostCapture}
                seatDisplayPercents={(slot, off) =>
                  computeSeatDisplayPercents(slot, off, {
                    belotIgraEllipse: effectiveLayoutPreset !== 'mobile-stack',
                  })
                }
                seatDisplayName={seatDisplayName}
                seatDealSlotRevealed={seatDealSlotRevealed}
                roundDealAnimActive={roundDealAnimActive}
                oppTurnClock={oppTurnClock}
                lastSeatActions={lastSeatActions}
                auctionSeatActions={auctionSeatActions}
                auctionLog={auctionLog}
                auctionLogOpen={auctionLogOpen}
                setAuctionLogOpen={setAuctionLogOpen}
                teamOfSeat={teamOfSeat}
                trickAnim={trickAnim}
                playsToTrickUiRows={playsToTrickUiRows}
                trickUi={trickUi}
                trickCenterRef={trickCenterRef}
                tableRevealAnchorRef={trickCenterRef}
                heroSeatAnchorRef={heroSeatAnchorRef}
                trickOverlayCardRefs={trickOverlayCardRefs}
                lastTrickSnapshot={lastTrickSnapshot}
                humanHandSorted={humanHandSorted}
                handFanMetrics={handFanMetrics}
                hoveredCardIndex={hoveredCardIndex}
                setHoveredCardIndex={setHoveredCardIndex}
                legalCardIds={legalCardIds}
                isHumanPlayTurn={isHumanPlayTurn}
                humanFirstPlayCommitPending={humanFirstPlayCommitPending}
                seatNodeRefsForDeal={seatNodeRefsForDeal}
                handSlotRefs={handSlotRefs}
                cardIdToPlaying={cardIdToPlaying}
                onPlayCard={onHeroCardClick}
                onHeroCardPointerDown={onHeroCardPointerDown}
                onHeroCardPointerMove={onHeroCardPointerMove}
                onHeroCardPointerUp={onHeroCardPointerUp}
                onHeroCardPointerCancel={onHeroCardPointerCancel}
                isHeroCardDragging={Boolean(dragPlayCard)}
                isHeroCardDropActive={Boolean(dragPlayCard?.dropActive)}
                draggingCardId={dragPlayCard?.cardId ?? null}
                draggingCardOriginIndex={dragPlayCard?.originIndex ?? null}
                pendingHiddenCardId={pendingHiddenCardId}
                onOpenScoreSheet={() => setScoreSheetOpen(true)}
                revealFallbackBanner={auctionRevealFallbackBanner}
                isInputBlocked={isInputBlocked}
                seatLayoutPreset={effectiveLayoutPreset}
                deckTrumpRevealMs={AUCTION_REVEAL_MS}
                declaredComboKeysBySeatTable={declaredComboKeysForStrip}
                declarationBattleOutcome={declarationStripBattleOutcome}
                declarationBattleApply={declarationStripBattleApply}
                belaSpeech={belaSpeech}
                comboRevealFx={comboRevealFx}
                prefersReducedMotionBelotFx={prefersReducedMotionFx}
                voidLeadCue={voidLeadCue}
                shulerTableFx={shulerTableFx}
                botEmoteCue={botEmoteCue}
                onAccuseDirtyPlay={onAccuseDirtyPlay}
                heroAuctionPanel={
                  isHumanAuctionTurn && !isInputBlocked ? (
                    <ArmenianAuctionPanel
                      open
                      presentation="inline"
                      highestBid={engineState.auction?.highestBid ?? null}
                      onPass={onHumanPass}
                      onBid={onHumanAuctionBid}
                      contraMultiplier={auctionContraMult}
                      canContra={canHumanContra}
                      canRecontra={canHumanRecontra}
                      auctionMultIsMax={auctionMultIsMax}
                      disabledAll={isInputBlocked}
                      onContra={onHumanContra}
                      onRecontra={onHumanRecontra}
                      playUi={play}
                      passLabel={isMoldovanMode ? 'PAS' : 'Пас'}
                      orderLabel={isMoldovanMode ? 'STRIGARE' : 'Заказать'}
                      turnHeadline={isMoldovanMode ? 'Ваш ход в bazar' : 'Ваш ход в торгах'}
                      variant={isMoldovanMode ? 'moldovan' : 'armenian'}
                      highestBidAuthorLabel={highestBidAuthorLabel}
                      highestBidAuthorRole={highestBidAuthorRole}
                    />
                  ) : null
                }
              />
              {revealPortal}

            <div className="sticky bottom-0 z-30 shrink-0 border-t border-border/50 bg-gradient-to-t from-background via-background to-transparent pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1 sm:pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pt-2">
              <div className="mx-auto w-full max-w-lg space-y-2 px-2">
                {roundIdle ? (
                  <button
                    type="button"
                    onClick={nextRound}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-950/35 py-3 text-sm font-bold text-emerald-100"
                  >
                    <RotateCw className="h-4 w-4" aria-hidden />
                    Следующий раунд
                  </button>
                ) : null}

                {bellaEligibleThisTurn ? (
                  <div className="flex items-center justify-center gap-2 rounded-xl border border-fuchsia-500/35 bg-fuchsia-950/20 px-2 py-2">
                    <button
                      type="button"
                      aria-pressed={bellaNextPlay}
                      onClick={() => setBellaNextPlay((v) => !v)}
                      className={cn(
                        'rounded-lg border px-3 py-2 text-[11px] font-bold transition',
                        bellaNextPlay
                          ? 'border-fuchsia-300/60 bg-fuchsia-600/35 text-fuchsia-50 shadow-[0_0_12px_rgba(217,70,239,0.35)]'
                          : 'border-fuchsia-500/30 bg-black/25 text-fuchsia-100/90 hover:border-fuchsia-400/50',
                      )}
                    >
                      {blotRebelotButtonLabel}
                    </button>
                    <span className="max-w-[14rem] text-[9px] leading-snug text-muted-foreground">
                      {isMoldovanMode
                        ? bellaNextPlay
                          ? 'Следующий ваш ход козырным королём или дамой объявит беллу (если карта легальна).'
                          : 'Включите, если следующим ходом играете козырного короля или даму и хотите объявить беллу.'
                        : bellaNextPlay
                          ? 'Следующий ход козырным K или Q зачтёт Блот или Реблот (+20), если карта легальна.'
                          : 'Включите, если следующим ходом играете козырного короля или даму и хотите объявить Блот/Реблот.'}
                    </span>
                  </div>
                ) : null}

                
              </div>
            </div>
            </div>

            {cardFly ? (
              <div
                ref={flyCardWrapRef}
                className="pointer-events-none fixed z-[200]"
                aria-hidden
              >
                <PlayingCard
                  rank={cardFly.rank}
                  suit={cardFly.suit}
                  size="md"
                  className="shadow-2xl"
                  faceDown={Boolean(cardFly.faceDown)}
                />
              </div>
            ) : null}

            {dragPlayCard ? (
              <div
                ref={dragGhostRef}
                className="pointer-events-none fixed z-[205]"
                style={{
                  left: dragPlayCard.x,
                  top: dragPlayCard.y,
                  transform:
                    `translate(-50%, -64%) rotate(5deg) scale(${dragPlayCard.returning ? 1 : 1.04})`,
                  filter: dragPlayCard.dropActive
                    ? 'drop-shadow(0 0 24px rgba(34,211,238,0.55))'
                    : 'drop-shadow(0 0 18px rgba(0,0,0,0.45))',
                }}
                aria-hidden
              >
                <PlayingCard
                  rank={dragPlayCard.rank}
                  suit={dragPlayCard.suit}
                  size="md"
                  className={cn(
                    'shadow-2xl transition-all duration-75',
                    dragPlayCard.dropActive && 'ring-2 ring-cyan-300/80 ring-offset-2 ring-offset-black/25',
                  )}
                />
              </div>
            ) : null}

            {inDeclare && !auctionRevealFx && !declareVoteModalBlocked ? (
              <div
                className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="declare-ready-vote-title"
              >
                <div className="relative w-full max-w-xl max-h-[min(88vh,42rem)] overflow-y-auto overscroll-contain rounded-2xl border border-amber-300/55 bg-gradient-to-b from-amber-950/96 via-[#191427]/96 to-[#0c1420]/98 p-4 shadow-[0_0_45px_rgba(251,191,36,0.28)] sm:p-5">
                  <div
                    className="pointer-events-none absolute inset-0 rounded-2xl border border-amber-200/20 animate-pulse"
                    aria-hidden
                  />
                  <h2
                    id="declare-ready-vote-title"
                    className="relative text-center text-sm font-black uppercase tracking-[0.13em] text-amber-100 sm:text-base"
                  >
                    Голосование: к взяткам
                  </h2>
                  <p className="relative mt-2 text-center text-[12px] font-semibold text-amber-100/90">
                    Торги закрыты: <span className="font-mono font-black text-amber-50">{formatAuctionBid(engineState.auction?.highestBid)}</span>
                  </p>
                  {(Number(engineState.contract?.contraMultiplier) || 1) > 1 ? (
                    <p className="relative mt-1 text-center text-[12px] text-amber-100/90">
                      Множитель: <span className="font-mono font-black">×{engineState.contract?.contraMultiplier ?? 1}</span>
                    </p>
                  ) : null}
                  {isMoldovanMode && moldovanDeclarationCompare ? (
                    <div className="relative mt-3 rounded-xl border border-cyan-400/30 bg-cyan-950/20 px-3 py-2 text-[11px] text-cyan-100/95">
                      <p className="text-center font-bold uppercase tracking-wide text-cyan-100">
                        Сравнение деклараций
                      </p>
                      <p className="mt-1 text-center">
                        <span className={cn(
                          'font-semibold',
                          moldovanDeclarationCompare.precedence.winner === 'teamB' &&
                            moldovanDeclarationCompare.teamASeqTotal > moldovanDeclarationCompare.precedence.scoreA &&
                            'text-rose-200 line-through decoration-2 decoration-rose-400/80',
                        )}>
                          Команда A: {moldovanDeclarationCompare.precedence.scoreA}
                        </span>
                        {' · '}
                        <span className={cn(
                          'font-semibold',
                          moldovanDeclarationCompare.precedence.winner === 'teamA' &&
                            moldovanDeclarationCompare.teamBSeqTotal > moldovanDeclarationCompare.precedence.scoreB &&
                            'text-rose-200 line-through decoration-2 decoration-rose-400/80',
                        )}>
                          Команда B: {moldovanDeclarationCompare.precedence.scoreB}
                        </span>
                      </p>
                      <p className="mt-1 text-center text-cyan-50">
                        {moldovanDeclarationCompare.precedence.winner === 'teamA'
                          ? `Старшая последовательность у команды A (${formatSequenceReason(moldovanDeclarationCompare.topA)}).`
                          : moldovanDeclarationCompare.precedence.winner === 'teamB'
                            ? `Старшая последовательность у команды B (${formatSequenceReason(moldovanDeclarationCompare.topB)}).`
                            : 'Старшинство последовательностей равное: очки не сгорают.'}
                      </p>
                      <p className="mt-0.5 text-center text-cyan-100/85">
                        {moldovanDeclarationCompare.precedence.winner === 'teamA'
                          ? 'Очки последовательностей команды B сгорели.'
                          : moldovanDeclarationCompare.precedence.winner === 'teamB'
                            ? 'Очки последовательностей команды A сгорели.'
                            : 'Очки последовательностей сохранены у обеих команд.'}
                      </p>
                    </div>
                  ) : null}
                  <div className="relative mt-3 rounded-xl border border-cyan-400/35 bg-cyan-950/20 px-3 py-2 text-center">
                    <span className="text-[11px] text-cyan-100/90">Автоподтверждение через </span>
                    <span className="font-mono text-lg font-black tabular-nums text-cyan-100">
                      {declareVoteSecondsLeft}
                    </span>
                    <span className="text-[11px] text-cyan-100/90"> сек</span>
                  </div>
                  <div className="relative mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {[0, 1, 2, 3].map((seat) => {
                      const ready = Boolean(declareVoteReadyBySeat[seat]);
                      const isMe = seat === HUMAN_SEAT;
                      return (
                        <div
                          key={`declare-ready-seat-${seat}`}
                          className={cn(
                            'flex items-center justify-between rounded-lg border px-3 py-2',
                            ready
                              ? 'border-emerald-400/45 bg-emerald-950/25 text-emerald-100'
                              : 'border-slate-500/35 bg-slate-950/35 text-slate-200',
                          )}
                        >
                          <span className="truncate text-[11px] font-semibold">
                            {seatDisplayName(seat, heroName)}
                            {isMe ? ' (Вы)' : ''}
                          </span>
                          <span className="text-[10px] font-black uppercase tracking-wide">
                            {ready ? 'Принято' : 'Ждём'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={onAcceptDeclareVote}
                    disabled={Boolean(declareVoteReadyBySeat[HUMAN_SEAT])}
                    className={cn(
                      'relative mt-4 w-full rounded-xl border py-3 text-sm font-black uppercase tracking-wide transition',
                      declareVoteReadyBySeat[HUMAN_SEAT]
                        ? 'cursor-default border-emerald-300/55 bg-emerald-700/35 text-emerald-50'
                        : 'border-amber-300/55 bg-amber-600/20 text-amber-50 hover:border-amber-200/70 hover:bg-amber-500/25',
                    )}
                  >
                    {declareVoteReadyBySeat[HUMAN_SEAT] ? 'Вы готовы' : 'Готов'}
                  </button>
                </div>
              </div>
            ) : null}

            {auctionRevealFx ? (
              (() => {
                const trumpView = largeTrumpDisplay(auctionRevealFx.trump);
                const trumpOrbToneClass =
                  trumpView.line1 === '♥'
                    ? 'belot-auction-reveal-orb-heart'
                    : trumpView.line1 === '♦'
                      ? 'belot-auction-reveal-orb-diamond'
                      : trumpView.line1 === '♠'
                        ? 'belot-auction-reveal-orb-spade'
                        : trumpView.line1 === '♣'
                          ? 'belot-auction-reveal-orb-club'
                          : trumpView.line1 === 'БК'
                            ? 'belot-auction-reveal-orb-notrump'
                            : '';
                const fromX = Number(auctionRevealFx?.fromX || 0);
                const fromY = Number(auctionRevealFx?.fromY || 0);
                const revealDistance = Math.hypot(fromX, fromY);
                const revealAngleDeg = (Math.atan2(-fromY, -fromX) * 180) / Math.PI;
                return (
                  <div className="belot-auction-reveal-backdrop fixed inset-0 z-[235] flex items-center justify-center p-4">
                    <div
                      className="belot-auction-reveal-shell"
                      style={{
                        '--belot-reveal-from-x': `${fromX.toFixed(2)}px`,
                        '--belot-reveal-from-y': `${fromY.toFixed(2)}px`,
                        '--belot-reveal-distance': `${revealDistance.toFixed(2)}`,
                        '--belot-reveal-angle': `${revealAngleDeg.toFixed(2)}deg`,
                      }}
                    >
                      <span className="belot-auction-reveal-trail" aria-hidden />
                      <span className="belot-auction-reveal-sheen" aria-hidden />
                      <p className="belot-auction-reveal-kicker">Контракт объявлен</p>
                      <div className="belot-auction-reveal-main">
                        <span className="belot-auction-reveal-points">{auctionRevealFx.points}</span>
                        <span
                          className={cn(
                            'belot-auction-reveal-orb',
                            trumpOrbToneClass,
                          )}
                          title={trumpView.line2 || undefined}
                        >
                          <span className="belot-auction-reveal-impact" aria-hidden />
                          <span className="belot-auction-reveal-orb-ring belot-auction-reveal-orb-ring-a" aria-hidden />
                          <span className="belot-auction-reveal-orb-ring belot-auction-reveal-orb-ring-b" aria-hidden />
                          <span className="belot-auction-reveal-orb-core">{trumpView.line1}</span>
                        </span>
                      </div>
                      {auctionRevealFx.multiplier > 1 ? (
                        <p
                          className={cn(
                            'belot-auction-reveal-multiplier',
                            auctionRevealFx.multiplier >= 4 && 'belot-auction-reveal-multiplier-high',
                          )}
                        >
                          Множитель ×{auctionRevealFx.multiplier}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })()
            ) : null}

            <BelotHardcoreDeclareCombosPanel
              open={hardcoreDeclDialogOpen && hardcoreDialogCombos.length > 0}
              combos={hardcoreDialogCombos}
              onCommitKeys={(keys) => confirmHardcoreDeclarations(keys)}
              playUi={play}
            />

            <ArmenianBelotScoreSheetPanel
              open={scoreSheetOpen}
              onClose={() => setScoreSheetOpen(false)}
              targetScore={engineState.targetScore ?? targetScore}
              rows={scoreSheetRows}
            />

            <ArmenianBelotEngineDebugPanel
              open={engineDebugOpen}
              onClose={() => setEngineDebugOpen(false)}
              engineState={engineState}
              history={engineDebugLog}
              onClearHistory={() => setEngineDebugLog([])}
              scoreSheetRows={scoreSheetRows}
              lastRoundSummary={lastRoundSummary}
            />
          </>
        )}
      </main>
      {entryDebitToast ? (
        <BelotCurrencyToast
          message={entryDebitToast}
          kind="debit"
          onClose={() => setEntryDebitToast('')}
        />
      ) : null}
      {leaveConfirmOpen ? (
        <div className="fixed inset-0 z-[230] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-rose-400/45 bg-[#120e1f]/95 p-4 shadow-2xl">
            <p className="text-center text-sm font-black uppercase tracking-wide text-rose-100">Покинуть матч?</p>
            <p className="mt-2 text-center text-xs text-slate-300">
              Выход из текущего матча засчитывается как поражение и полная потеря ставки.
            </p>
            <p className="mt-1.5 text-center text-xs font-semibold text-rose-200">
              Потеря при выходе: -{currentPlayerStake.toLocaleString('ru-RU')} фишек
            </p>
            {leaveError ? (
              <p className="mt-2 rounded-lg border border-rose-300/45 bg-rose-900/30 px-2 py-1.5 text-center text-[11px] text-rose-100">
                {leaveError}
              </p>
            ) : null}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setLeaveConfirmOpen(false)}
                className="rounded-xl border border-white/15 py-2 text-xs font-semibold text-slate-200"
              >
                Остаться
              </button>
              <button
                type="button"
                disabled={economyBusy}
                onClick={confirmLeaveWithForfeit}
                className="rounded-xl border border-rose-400/55 bg-rose-900/40 py-2 text-xs font-bold text-rose-100 disabled:opacity-60"
              >
                {economyBusy ? 'Выход...' : `Выйти и потерять ${currentPlayerStake.toLocaleString('ru-RU')}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function BelotArmenianLocalLobby(props) {
  return <BelotArmenianLocalLobbyInner {...props} />;
}
